/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useCallback, useRef, useEffect } from "react";
import {
	Button,
	TextField,
	IconButton,
	Paper,
	Box,
	Tooltip,
	Menu,
	ListItemIcon,
	ListItemText,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import TitleIcon from "@material-ui/icons/Title";
import ImageIcon from "@material-ui/icons/Image";
import VideoLibraryIcon from "@material-ui/icons/VideoLibrary";
import LiveHelpIcon from "@material-ui/icons/LiveHelp";
import CropSquareIcon from "@material-ui/icons/CropSquare";
import PanoramaFishEyeIcon from "@material-ui/icons/PanoramaFishEye";
import ChangeHistoryIcon from "@material-ui/icons/ChangeHistory";
import RemoveIcon from "@material-ui/icons/Remove";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import AssignmentIcon from "@material-ui/icons/Assignment";
import FlipToFrontIcon from "@material-ui/icons/FlipToFront";
import FlipToBackIcon from "@material-ui/icons/FlipToBack";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ControlPointDuplicateIcon from "@material-ui/icons/ControlPointDuplicate";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import CategoryIcon from "@material-ui/icons/Category";
import TouchAppIcon from "@material-ui/icons/TouchApp";
import PermMediaIcon from "@material-ui/icons/PermMedia";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import EditIcon from "@material-ui/icons/Edit";
import PaletteIcon from "@material-ui/icons/Palette";
import DesktopWindowsIcon from "@material-ui/icons/DesktopWindows";
import TabletIcon from "@material-ui/icons/Tablet";
import PhoneIphoneIcon from "@material-ui/icons/PhoneIphone";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import CloseIcon from "@material-ui/icons/Close";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import AspectRatioIcon from "@material-ui/icons/AspectRatio";
import SettingsIcon from "@material-ui/icons/Settings";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import { Select, MenuItem, FormControl, InputLabel, Slider, FormControlLabel, Switch } from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { SERVER_URL } from "../../../../utils";
import { alertError } from "../../../../utils/logger";
import { getPlantillas } from "../../../../api/plantilla";
import { getQuizzes, createQuiz, updateQuiz } from "../../../../api/quiz";
import { getUserMedia, uploadUserMedia } from "../../../../api/files";
import InlineQuizEditor from "./InlineQuizEditor";
import RgbaColorPickerPopover from "./RgbaColorPickerPopover";
import HexColorPickerPopover from "./HexColorPickerPopover";
import ShapeColorPicker from "./ShapeColorPicker";

const CANVAS_WIDTH = 960;
const CANVAS_HEIGHT = 540;

const PAGE_TRANSITIONS = [
	{ id: "none", label: "Sin animación" },
	{ id: "fade", label: "Fundido" },
	{ id: "slideLeft", label: "Deslizar izquierda" },
	{ id: "slideRight", label: "Deslizar derecha" },
	{ id: "slideUp", label: "Deslizar arriba" },
	{ id: "slideDown", label: "Deslizar abajo" },
	{ id: "zoom", label: "Zoom" },
];

const TRANSITION_SPEEDS = [
	{ ms: 150, label: "Muy rápida" },
	{ ms: 250, label: "Rápida" },
	{ ms: 350, label: "Normal" },
	{ ms: 500, label: "Lenta" },
	{ ms: 700, label: "Muy lenta" },
];

const VIEWPORTS = [
	{ id: "desktop", label: "Escritorio", shortLabel: "Escritorio", Icon: DesktopWindowsIcon, width: 960, height: 540 },
	{ id: "tablet", label: "Tablet", shortLabel: "Tablet", Icon: TabletIcon, width: 768, height: 540 },
	{ id: "mobile", label: "Móvil", shortLabel: "Móvil", Icon: PhoneIphoneIcon, width: 375, height: 667 },
];

const MODAL_SIZES = [
	{ id: "small", label: "Pequeño", width: 400, height: 300 },
	{ id: "medium", label: "Mediano", width: 640, height: 480 },
	{ id: "large", label: "Grande", width: 800, height: 600 },
];

const DESKTOP = { width: 960, height: 540 };

function getElementsForViewport(page, viewportId) {
	const baseElements = page?.elements || [];
	if (viewportId === "desktop") return baseElements;
	const vp = VIEWPORTS.find((v) => v.id === viewportId);
	if (!vp) return baseElements;
	const scaleX = vp.width / DESKTOP.width;
	const scaleY = vp.height / DESKTOP.height;
	const scale = Math.min(scaleX, scaleY);
	return baseElements.map((el) => {
		const override = el?.breakpoints?.[viewportId];
		if (override && typeof override.x === "number") {
			return { ...el, x: override.x, y: override.y ?? el.y, width: override.width ?? el.width, height: override.height ?? el.height };
		}
		return {
			...el,
			x: Math.round((el.x || 0) * scale),
			y: Math.round((el.y || 0) * scale),
			width: Math.round((el.width || 100) * scale),
			height: Math.round((el.height || 50) * scale),
		};
	});
}

const genId = () => `el_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const defaultElement = (type) => {
	const shapeType = type.startsWith("shape-") ? type.replace("shape-", "") : null;
	const isLine = type === "shape-line";
	const base = {
		elementId: genId(),
		type: shapeType ? "shape" : type,
		x: 50,
		y: 50,
		width: shapeType ? (isLine ? 150 : 100) : 200,
		height: shapeType ? (isLine ? 8 : 100) : 80,
	};
	if (type === "text")
		return {
			...base,
			content: { html: "<p>Nuevo texto</p>" },
			style: {
				fontFamily: "Arial, sans-serif",
				fontSize: 18,
				color: "#000000",
				backgroundColor: "transparent",
				fontWeight: "normal",
				fontStyle: "normal",
				textDecoration: "none",
				textAlign: "left",
			},
		};
	if (type === "image")
		return { ...base, content: { url: "", alt: "" } };
	if (type === "video")
		return { ...base, content: { url: "" }, width: 400, height: 225 };
	if (type === "quiz")
		return { ...base, content: { quizId: null } };
	if (type === "button")
		return {
			...base,
			width: 160,
			height: 44,
			content: { action: "nextPage", label: "Siguiente", icon: "", iconPosition: "before" },
			style: {
				fontFamily: "Arial, sans-serif",
				fontSize: 16,
				fontWeight: "bold",
				color: "rgba(51, 51, 51, 1)",
				backgroundColor: "transparent",
				textAlign: "center",
				borderRadius: 8,
			},
		};
	if (type === "hint")
		return {
			...base,
			width: 120,
			height: 40,
			content: { label: "Pista", hintText: "", hintPenalty: 0 },
			style: {
				fontFamily: "Arial, sans-serif",
				fontSize: 14,
				fontWeight: "bold",
				color: "rgba(124, 58, 237, 1)",
				backgroundColor: "rgba(245, 243, 255, 1)",
				borderColor: "rgba(124, 58, 237, 1)",
				borderWidth: 1,
				textAlign: "center",
				borderRadius: 8,
			},
		};
	if (shapeType) {
		return {
			...base,
			content: {
				shapeType,
				fillColor: "rgba(100, 100, 255, 0.8)",
				strokeColor: "rgba(0, 0, 0, 1)",
				strokeWidth: 2,
				borderRadius: 0,
			},
		};
	}
	return base;
};

const parseRgba = (str) => {
	const m = str && str.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
	if (m) return { r: +m[1], g: +m[2], b: +m[3], a: m[4] !== undefined ? +m[4] : 1 };
	return { r: 0, g: 0, b: 0, a: 1 };
};
const toRgba = (r, g, b, a) => `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${a})`;

const SHAPE_TYPES = [
	{ value: "square", label: "Cuadrado" },
	{ value: "circle", label: "Círculo" },
	{ value: "triangle", label: "Triángulo" },
	{ value: "line", label: "Línea" },
];

const FONT_OPTIONS = [
	"League Spartan, sans-serif",
	"Arial, sans-serif",
	"Georgia, serif",
	"Times New Roman, serif",
	"Verdana, sans-serif",
	"Courier New, monospace",
	"Impact, sans-serif",
	"Comic Sans MS, cursive",
];

const HANDLE_SIZE = 8;
const MIN_SIZE = 40;

const resizeHandleStyle = (pos, inset = false) => {
	const offset = inset ? 0 : -HANDLE_SIZE / 2;
	return {
		position: "absolute",
		width: HANDLE_SIZE,
		height: HANDLE_SIZE,
		backgroundColor: "#1976d2",
		border: "1px solid #fff",
		borderRadius: 2,
		zIndex: 10,
		cursor:
			pos === "se" || pos === "nw"
				? "nwse-resize"
				: pos === "sw" || pos === "ne"
				? "nesw-resize"
				: pos === "n" || pos === "s"
				? "ns-resize"
				: "ew-resize",
		...(pos === "nw" && { left: offset, top: offset }),
		...(pos === "ne" && { right: offset, top: offset }),
		...(pos === "sw" && { left: offset, bottom: offset }),
		...(pos === "se" && { right: offset, bottom: offset }),
	};
};

function resolveQuizImageUrl(url) {
	if (!url || !url.trim()) return null;
	if (url.startsWith("http") || url.startsWith("/") || url.startsWith("data:")) return url;
	return `${SERVER_URL}/${url}`;
}

const isShapeGradient = (v) => v && String(v).trim().startsWith("linear-gradient");

function ElementRenderer({ element, selected, onSelect, onUpdate, onUpdateQuiz, onDragStart, onResizeStart, preview, isLocked, registrationDesign, canvasZoom = 1, onButtonAction, quizData, simulateQuiz, playQuizAnswer, onQuizOptionSelect, onRequestOptionImagePicker, onQuizHintUsed, onHintPreviewClick, onHintPlayModal }) {
	const editRef = useRef(null);
	const lastTextClickRef = useRef({ time: 0, id: null });
	const [hintModal, setHintModal] = useState(null);
	const locked = isLocked || (preview && false);

	useEffect(() => {
		if (element.type === "text" && editRef.current && element.content) {
			const html = element.content.html || `<p>${element.content.text || "Texto"}</p>`;
			if (editRef.current.innerHTML !== html) {
				editRef.current.innerHTML = html;
			}
		}
	}, [element.elementId, element.type, element.content?.html, element.content?.text]);

	useEffect(() => {
		if (element.type !== "text" || preview || locked) return;
		const measure = () => {
			if (editRef.current) {
				const rect = editRef.current.getBoundingClientRect();
				const w = Math.ceil(rect.width / canvasZoom);
				const h = Math.ceil(rect.height / canvasZoom);
				if (w > 0 && h > 0 && (element.width !== w || element.height !== h)) {
					onUpdate({ ...element, width: w, height: h });
				}
			}
		};
		const t = setTimeout(measure, 10);
		return () => clearTimeout(t);
	}, [element.type, element.content?.html, element.style?.fontSize, element.style?.fontFamily, element.style?.fontWeight, element.style?.fontStyle, preview, locked, element, onUpdate, canvasZoom]);

	const hasTransparentBg = element.type === "shape" || element.type === "text" || element.type === "image" || element.type === "hint" || element.type === "button";
	const style = {
		position: "absolute",
		left: element.x,
		top: element.y,
		width: element.width,
		height: element.height,
		border: preview ? "none" : locked ? "none" : selected ? "2px solid #1976d2" : "1px dashed #ccc",
		borderRadius: 4,
		cursor: preview || locked ? "default" : selected ? "move" : "pointer",
		overflow: "hidden",
		backgroundColor: hasTransparentBg ? "transparent" : "#fff",
		boxSizing: "border-box",
	};

	const handleMouseDown = (e) => {
		if (preview || locked) return;
		if (e.button !== 0) return;
		e.stopPropagation();
		e.preventDefault();
		if (e.target.closest(".element-resize-handle")) return;
		onSelect(element);
		onDragStart(element, e);
	};

	const useInsetHandles = element.type === "video";
	const ResizeHandles = () =>
		preview || locked || !selected || element.type === "text"
			? null
			: (
				<>
					{["nw", "ne", "sw", "se"].map((pos) => (
						<div
							key={pos}
							className="element-resize-handle"
							style={resizeHandleStyle(pos, useInsetHandles)}
							onMouseDown={(e) => {
								if (e.button !== 0) return;
								e.stopPropagation();
								e.preventDefault();
								onResizeStart(element, pos, e);
							}}
						/>
					))}
				</>
			);

	if (element.type === "text") {
		const s = element.style || {};
		const textContentStyle = {
			padding: 8,
			fontFamily: s.fontFamily || "Arial, sans-serif",
			fontSize: s.fontSize || 18,
			color: s.color || "#000",
			backgroundColor: s.backgroundColor === "transparent" ? "transparent" : s.backgroundColor || "transparent",
			fontWeight: s.fontWeight || "normal",
			fontStyle: s.fontStyle || "normal",
			textDecoration: s.textDecoration || "none",
			textAlign: s.textAlign || "left",
			display: "inline-block",
			width: "fit-content",
			minWidth: 20,
			minHeight: 24,
			outline: "none",
			boxSizing: "border-box",
		};

		const scheduleMeasure = () => {
			setTimeout(() => {
				if (editRef.current && !preview && !locked) {
					const rect = editRef.current.getBoundingClientRect();
					const w = Math.ceil(rect.width / canvasZoom);
					const h = Math.ceil(rect.height / canvasZoom);
					if (w > 0 && h > 0 && (element.width !== w || element.height !== h)) {
						onUpdate({ ...element, width: w, height: h });
					}
				}
			}, 0);
		};

		const handleTextMouseDown = (e) => {
			if (preview || locked) return;
			if (e.button !== 0) return;
			e.stopPropagation();
			if (!locked) onSelect(element);
			const now = Date.now();
			const isDoubleClickIntent = lastTextClickRef.current.id === element.elementId && now - lastTextClickRef.current.time < 400;
			lastTextClickRef.current = { time: now, id: element.elementId };
			if (!isDoubleClickIntent) onDragStart(element, e);
		};
		const handleTextDoubleClick = (e) => {
			e.stopPropagation();
			if (preview || locked) return;
			onSelect(element);
			setTimeout(() => editRef.current?.focus(), 0);
		};
		return (
			<div
				style={{ ...style, width: "fit-content", height: "fit-content" }}
				onMouseDown={handleTextMouseDown}
				onDoubleClick={handleTextDoubleClick}
				onClick={(e) => {
					e.stopPropagation();
					if (!locked) onSelect(element);
				}}
			>
				<ResizeHandles />
				<div
					ref={editRef}
					className="element-text-content"
					contentEditable={preview || locked ? false : selected}
					suppressContentEditableWarning
					style={textContentStyle}
					onBlur={preview ? undefined : (e) => {
						onUpdate({ ...element, content: { ...element.content, html: e.target.innerHTML } });
						scheduleMeasure();
					}}
					onInput={preview || locked ? undefined : scheduleMeasure}
				/>
			</div>
		);
	}
	if (element.type === "image") {
		const isLogo = element.registrationRole === "logo" || element.registrationRole === "logoOncommit" || element.registrationRole === "logoJandJ";
		const imgUrl = isLogo && registrationDesign?.logoUrl
			? registrationDesign.logoUrl
			: element.content?.url;
		return (
			<div
				style={style}
				onMouseDown={handleMouseDown}
				onClick={(e) => { e.stopPropagation(); if (!locked) onSelect(element); }}
			>
				<ResizeHandles />
				{imgUrl ? (
					<img
						src={imgUrl.startsWith("http") || imgUrl.startsWith("/") || imgUrl.startsWith("data:") ? imgUrl : `${SERVER_URL}/${imgUrl}`}
						alt={element.content?.alt || ""}
						style={{
							width: "100%",
							height: "100%",
							objectFit: isLogo ? "contain" : "cover",
							opacity: element.style?.opacity != null ? element.style.opacity : 1,
							borderRadius: element.style?.borderRadiusPercent != null ? `${element.style.borderRadiusPercent}%` : 0,
						}}
					/>
				) : (
					<div style={{ padding: 8, color: "#999", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
						{isLogo ? "Logo" : selected ? "URL en panel derecho" : "Imagen"}
					</div>
				)}
			</div>
		);
	}
	if (element.type === "video") {
		const videoStyle = { ...style };
		if (selected && !preview && !locked) videoStyle.overflow = "visible";
		const videoWrapperStyle = {
			position: "absolute",
			inset: 0,
			zIndex: 0,
			pointerEvents: preview ? "none" : (selected && !locked ? "none" : "auto"),
		};
		return (
			<div
				style={videoStyle}
				onMouseDown={handleMouseDown}
				onClick={(e) => { e.stopPropagation(); if (!locked) onSelect(element); }}
			>
				<div style={videoWrapperStyle}>
					{preview ? (
						element.content?.url ? (
							<video
								src={
									element.content.url.startsWith("http") || element.content.url.startsWith("/") || element.content.url.startsWith("data:")
										? element.content.url
										: `${SERVER_URL}/${element.content.url}`
								}
								preload="metadata"
								muted
								playsInline
								style={{ width: "100%", height: "100%", objectFit: "contain" }}
							/>
						) : (
							<div style={{ padding: 8, color: "#999", fontSize: 12, width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#eee" }}>
								<VideoLibraryIcon style={{ fontSize: 24, color: "#999" }} />
							</div>
						)
					) : element.content?.url ? (
						<video
							src={
								element.content.url.startsWith("http") || element.content.url.startsWith("/") || element.content.url.startsWith("data:")
									? element.content.url
									: `${SERVER_URL}/${element.content.url}`
							}
							controls={!selected}
							preload="metadata"
							playsInline
							autoPlay={!!element.content?.autoplay}
							muted={!!element.content?.autoplay}
							style={{
								width: "100%",
								height: "100%",
								objectFit: "contain",
							}}
						/>
					) : (
						<div style={{ padding: 8, color: "#999", fontSize: 12 }}>
							{selected ? "URL en panel derecho" : "Video"}
						</div>
					)}
				</div>
				<ResizeHandles />
			</div>
		);
	}
	if (element.type === "quiz") {
		const quiz = quizData;
		const questions = quiz?.questions || [];
		const firstQ = questions[0];
		const opts = firstQ?.options || [];
		const hasQuizContent = quiz && questions.length > 0;
		const answer = simulateQuiz ? (playQuizAnswer || {})[element.elementId] : null;
		const answered = answer != null;
		const isSimulating = simulateQuiz && hasQuizContent;
		const canEdit = selected && !locked && !simulateQuiz && hasQuizContent && onUpdateQuiz && quiz?._id;

		// FIX: detectar tipo imagen aquí, fuera del IIFE, para usarlo en toda la lógica
		const isImageType = firstQ?.type === "imageChoice" || firstQ?.type === "optionsWithImages";

		const qStyle = element.style || {};
		const optStyle = element.content?.quizOptionsStyle || {};
		const textStyle = {
			fontFamily: qStyle.fontFamily || "Arial, sans-serif",
			fontSize: qStyle.fontSize || 14,
			color: qStyle.color || "#1e293b",
			fontWeight: qStyle.fontWeight || "600",
			fontStyle: qStyle.fontStyle || "normal",
			textDecoration: qStyle.textDecoration || "none",
			textAlign: qStyle.textAlign || "left",
		};
		const imageMaxHeight = Math.max(40, Math.min(400, element.content?.quizOptionsImageMaxHeight ?? 120));
		const optionTextStyle = {
			fontFamily: optStyle.fontFamily || "Arial, sans-serif",
			fontSize: optStyle.fontSize || 12,
			color: optStyle.color || "#334155",
			fontWeight: optStyle.fontWeight || "normal",
			fontStyle: optStyle.fontStyle || "normal",
			textDecoration: optStyle.textDecoration || "none",
			textAlign: optStyle.textAlign || "left",
		};
		const handleQuestionBlur = (e) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const newText = (e.target.textContent || "").trim() || "Pregunta";
			if (newText === (firstQ?.text || "")) return;
			onUpdateQuiz(quiz._id, {
				fullName: newText.slice(0, 80),
				questions: [{
					type: firstQ.type,
					text: newText,
					optionsLayout: firstQ.optionsLayout,
					gridColumns: firstQ.gridColumns,
					options: (firstQ.options || []).map((o) => ({ text: o.text, imageUrl: o.imageUrl || "", correct: !!o.correct })),
					explanation: firstQ.explanation || "",
				}],
				pointsCorrect: quiz.pointsCorrect,
				pointsIncorrect: quiz.pointsIncorrect,
				allowMultipleAttempts: quiz.allowMultipleAttempts,
			});
		};
		const handleOptionBlur = (i) => (e) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const newText = (e.target.textContent || "").trim() || `Opción ${i + 1}`;
			const current = opts[i]?.text || "";
			if (newText === current) return;
			const newOpts = opts.map((o, j) => (j === i ? { text: newText, imageUrl: o.imageUrl || "", correct: !!o.correct } : { text: o.text, imageUrl: o.imageUrl || "", correct: !!o.correct }));
			onUpdateQuiz(quiz._id, {
				fullName: quiz.fullName,
				questions: [{
					type: firstQ.type,
					text: firstQ.text || "",
					optionsLayout: firstQ.optionsLayout,
					gridColumns: firstQ.gridColumns,
					options: newOpts,
					explanation: firstQ.explanation || "",
				}],
				pointsCorrect: quiz.pointsCorrect,
				pointsIncorrect: quiz.pointsIncorrect,
				allowMultipleAttempts: quiz.allowMultipleAttempts,
			});
		};
		const handleOptionClick = isSimulating && !answered && onQuizOptionSelect
			? (i) => { onQuizOptionSelect(element.elementId, i, opts[i]?.correct); }
			: undefined;
		const handleSetCorrectOption = canEdit ? (i) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const isTrueFalse = firstQ.type === "trueFalse";
			const newOpts = isTrueFalse
				? opts.map((o, j) => ({ text: o.text, imageUrl: o.imageUrl || "", correct: j === i }))
				: (() => {
					const toggled = !opts[i]?.correct;
					if (!toggled && opts.filter((o) => o.correct).length <= 1) return opts;
					return opts.map((o, j) => ({ text: o.text, imageUrl: o.imageUrl || "", correct: j === i ? toggled : o.correct }));
				})();
			if (newOpts === opts) return;
			onUpdateQuiz(quiz._id, {
				fullName: quiz.fullName,
				questions: [{ type: firstQ.type, text: firstQ.text || "", optionsLayout: firstQ.optionsLayout, gridColumns: firstQ.gridColumns, options: newOpts, explanation: firstQ.explanation || "" }],
				pointsCorrect: quiz.pointsCorrect,
				pointsIncorrect: quiz.pointsIncorrect,
				allowMultipleAttempts: quiz.allowMultipleAttempts,
			});
		} : undefined;
		const handleAddOption = canEdit && firstQ.type !== "trueFalse" ? () => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const newOpts = [...opts, { text: "", imageUrl: "", correct: false }];
			onUpdateQuiz(quiz._id, {
				fullName: quiz.fullName,
				questions: [{ type: firstQ.type, text: firstQ.text || "", optionsLayout: firstQ.optionsLayout, gridColumns: firstQ.gridColumns, options: newOpts, explanation: firstQ.explanation || "" }],
				pointsCorrect: quiz.pointsCorrect,
				pointsIncorrect: quiz.pointsIncorrect,
				allowMultipleAttempts: quiz.allowMultipleAttempts,
			});
		} : undefined;
		const handleRemoveOption = canEdit && firstQ.type !== "trueFalse" && opts.length > 1 ? (i) => {
			if (!onUpdateQuiz || !quiz?._id) return;
			const hadCorrect = opts[i]?.correct;
			let newOpts = opts.filter((_, j) => j !== i);
			if (hadCorrect && newOpts.length > 0 && !newOpts.some((o) => o.correct)) {
				newOpts = newOpts.map((o, j) => (j === 0 ? { ...o, correct: true } : o));
			}
			onUpdateQuiz(quiz._id, {
				fullName: quiz.fullName,
				questions: [{ type: firstQ.type, text: firstQ.text || "", optionsLayout: firstQ.optionsLayout, gridColumns: firstQ.gridColumns, options: newOpts, explanation: firstQ.explanation || "" }],
				pointsCorrect: quiz.pointsCorrect,
				pointsIncorrect: quiz.pointsIncorrect,
				allowMultipleAttempts: quiz.allowMultipleAttempts,
			});
		} : undefined;
		const handleOptionImageClick = canEdit && (firstQ.type === "imageChoice" || firstQ.type === "optionsWithImages") && onRequestOptionImagePicker ? (i) => (e) => {
			e.stopPropagation();
			onRequestOptionImagePicker(element.elementId, i);
		} : undefined;
		return (
			<>
			<div
				style={{
					...style,
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					background: "#fff",
					borderRadius: 8,
					boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
					border: selected ? "2px solid #7c3aed" : "1px solid #e2e8f0",
				}}
				onMouseDown={isSimulating ? (e) => e.stopPropagation() : handleMouseDown}
				onClick={(e) => { e.stopPropagation(); if (!isSimulating && !locked) onSelect(element); }}
			>
				<ResizeHandles />
				{/* FIX: padding reducido para tipos imagen, sin gap extra que empuje contenido */}
				<div style={{
					flex: 1,
					overflow: "hidden",
					// FIX: para tipos imagen sin pregunta, quitamos padding top y gap
					padding: isImageType ? "6px 6px 6px 6px" : 10,
					display: "flex",
					flexDirection: "column",
					// FIX: gap 0 para imagen (las opciones ocupan todo el espacio), gap 8 para otros
					gap: isImageType ? 0 : 8,
					minHeight: 0,
					boxSizing: "border-box",
				}}>
					{hasQuizContent ? (
						<>
							{/* FIX: solo mostrar texto de la pregunta si NO es tipo imagen */}
							{!isImageType && firstQ.text && (
								<div style={{ ...textStyle, marginBottom: 4, flexShrink: 0 }}>
									{firstQ.text}
								</div>
							)}
							{(() => {
								const layout = firstQ.optionsLayout || "row";
								const gridCols = Math.min(6, Math.max(1, firstQ.gridColumns || 2));
								let containerStyle = {};
								if (isImageType) {
									if (layout === "columns") {
										// FIX: overflow hidden + height 100% para que no desborde
										containerStyle = {
											flex: 1,
											display: "flex",
											flexDirection: "row",
											flexWrap: "nowrap",
											gap: 6,
											minHeight: 0,
											minWidth: 0,
											overflow: "hidden",
											height: "100%",
										};
									} else if (layout === "row") {
										containerStyle = {
											flex: 1,
											display: "flex",
											flexDirection: "column",
											flexWrap: "nowrap",
											gap: 6,
											minHeight: 0,
											minWidth: 0,
											overflow: "hidden",
											height: "100%",
										};
									} else {
										// grid
										containerStyle = {
											flex: 1,
											display: "grid",
											gap: 6,
											minHeight: 0,
											minWidth: 0,
											overflow: "hidden",
											height: "100%",
											gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
											gridAutoRows: "minmax(0, 1fr)",
										};
									}
								} else {
									containerStyle = {
										flex: 1,
										display: firstQ.optionsLayout === "grid" ? "grid" : "flex",
										flexDirection: layout === "columns" ? "row" : "column",
										flexWrap: "nowrap",
										gridTemplateColumns: firstQ.optionsLayout === "grid" ? `repeat(${gridCols}, minmax(0, 1fr))` : undefined,
										gap: 6,
										minHeight: 0,
										minWidth: 0,
										overflow: "hidden",
									};
								}
								return (
							<div style={containerStyle}>
								{opts.map((o, i) => {
									const hasImg = o.imageUrl && o.imageUrl.trim();
									const isSelected = answered && answer.selectedIndex === i;
									const bg = answered
										? (o.correct ? "#f0fdf4" : isSelected ? "#fef2f2" : "#fff")
										: (isSimulating ? "#fff" : (o.correct ? "#f0fdf4" : "#fff"));
									const cardBg = isCardStyle ? "transparent" : bg;
									const borderColor = answered
										? (o.correct ? "#22c55e" : isSelected ? "#ef4444" : "#e2e8f0")
										: (isSimulating ? "#e2e8f0" : (o.correct ? "#22c55e" : "#e2e8f0"));
									const optionLabel = (firstQ.type === "imageChoice" && !o.text) ? `Opción ${i + 1}` : (o.text || "Opción");
									const isCardStyle = isImageType;
									return (
										<div
											key={i}
											role={isSimulating ? "button" : undefined}
											tabIndex={isSimulating ? 0 : undefined}
											onClick={handleOptionClick ? (e) => { handleOptionClick(i); e.currentTarget.blur(); } : undefined}
											onKeyDown={handleOptionClick ? (ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); handleOptionClick(i); ev.currentTarget.blur(); } } : undefined}
											style={isCardStyle ? {
												display: "flex",
												flexDirection: "column",
												position: "relative",
												borderRadius: 8,
												background: cardBg,
												border: "2px solid",
												borderColor,
												overflow: "hidden",
												boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
												cursor: handleOptionClick ? "pointer" : "default",
												boxSizing: "border-box",
												outline: "none",
												// FIX: flex 1 1 0 para que cada opción ocupe espacio igual sin desbordarse
												flex: "1 1 0",
												minWidth: 0,
												minHeight: 0,
											} : {
												display: "flex",
												alignItems: "center",
												gap: 6,
												padding: "6px 8px",
												borderRadius: 6,
												background: bg,
												border: "1px solid",
												borderColor,
												...optionTextStyle,
												cursor: handleOptionClick ? "pointer" : "default",
												minHeight: 28,
												flex: 1,
												minWidth: 0,
												boxSizing: "border-box",
												outline: "none",
											}}
										>
											{isCardStyle ? (
												<>
													{/* FIX: la imagen ocupa todo el flex del card - NO clicable; el cambio de imagen va en un icono aparte */}
													<div style={{ position: "relative", flex: 1, minHeight: 0, background: "transparent", overflow: "hidden" }}>
														<div style={{
															width: "100%",
															height: "100%",
															minHeight: 0,
															overflow: "hidden",
															background: "transparent",
															borderBottom: handleOptionImageClick && firstQ.type === "optionsWithImages" ? "2px dashed #cbd5e1" : "none",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
														}}>
															{hasImg ? (
																<img src={resolveQuizImageUrl(o.imageUrl)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />
															) : (
																<ImageIcon style={{ fontSize: 32, color: "#94a3b8" }} />
															)}
														</div>
														{handleSetCorrectOption && (
															<div style={{ position: "absolute", top: 4, right: 4, zIndex: 1, display: "flex", alignItems: "center", gap: 2 }}>
																{handleOptionImageClick && (
																	<IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOptionImageClick(i); }} style={{ padding: 2, background: "rgba(255,255,255,0.9)", color: "#64748b" }} title="Cambiar imagen">
																		<ImageIcon style={{ fontSize: 16 }} />
																	</IconButton>
																)}
																<IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSetCorrectOption(i); }} style={{ padding: 2, background: "rgba(255,255,255,0.9)", color: o.correct ? "#22c55e" : "#94a3b8" }} title={firstQ.type === "trueFalse" ? "Marcar como correcta" : "Marcar/desmarcar correcta"}>
																	{o.correct ? <CheckCircleIcon style={{ fontSize: 18 }} /> : <RadioButtonUncheckedIcon style={{ fontSize: 18 }} />}
																</IconButton>
															</div>
														)}
														{!handleSetCorrectOption && (
															<div style={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}>
																{(answered ? o.correct : !simulateQuiz && o.correct) && <span style={{ color: "#22c55e", background: "rgba(255,255,255,0.9)", borderRadius: 4, padding: 2 }}>✓</span>}
																{answered && isSelected && !o.correct && <span style={{ color: "#ef4444", background: "rgba(255,255,255,0.9)", borderRadius: 4, padding: 2 }}>✗</span>}
															</div>
														)}
													</div>
													{/* Solo mostrar texto en optionsWithImages, no en imageChoice */}
													{firstQ.type === "optionsWithImages" && (
														<div style={{
															padding: "4px 6px",
															...optionTextStyle,
															textAlign: "center",
															fontSize: (optionTextStyle.fontSize || 12) * 0.95,
															flexShrink: 0,
															background: "transparent",
														}}>
															{canEdit ? (
																<span
																	contentEditable
																	suppressContentEditableWarning
																	onBlur={handleOptionBlur(i)}
																	onClick={(e) => { e.stopPropagation(); if (handleOptionClick) e.preventDefault(); }}
																	onMouseDown={(e) => e.stopPropagation()}
																	style={{ outline: "none", cursor: "text", display: "block" }}
																>
																	{optionLabel}
																</span>
															) : (
																<span style={{ overflow: "hidden", textOverflow: "ellipsis", display: "block", whiteSpace: "nowrap" }}>{optionLabel}</span>
															)}
														</div>
													)}
													{handleRemoveOption && (
														<div style={{ position: "absolute", bottom: firstQ.type === "optionsWithImages" ? 28 : 4, right: 4, zIndex: 1 }}>
															<IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRemoveOption(i); }} style={{ padding: 2, background: "rgba(255,255,255,0.9)" }} title="Eliminar opción" disabled={opts.length <= 1}>
																<DeleteIcon style={{ fontSize: 16 }} />
															</IconButton>
														</div>
													)}
												</>
											) : (
												<>
													{handleSetCorrectOption ? (
														<IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSetCorrectOption(i); }} style={{ padding: 4, color: o.correct ? "#22c55e" : "#94a3b8" }} title={firstQ.type === "trueFalse" ? "Marcar como correcta" : "Marcar/desmarcar correcta"}>
															{o.correct ? <CheckCircleIcon style={{ fontSize: 20 }} /> : <RadioButtonUncheckedIcon style={{ fontSize: 20 }} />}
														</IconButton>
													) : (
														<span style={{ width: 20, flexShrink: 0, textAlign: "center", fontSize: 14 }}>
															{(answered ? o.correct : !simulateQuiz && o.correct) && <span style={{ color: "#22c55e" }}>✓</span>}
															{answered && isSelected && !o.correct && <span style={{ color: "#ef4444" }}>✗</span>}
														</span>
													)}
													{canEdit ? (
														<span
															contentEditable
															suppressContentEditableWarning
															onBlur={handleOptionBlur(i)}
															onClick={(e) => { e.stopPropagation(); if (handleOptionClick) e.preventDefault(); }}
															onMouseDown={(e) => e.stopPropagation()}
															style={{ flex: 1, overflow: "hidden", outline: "none", padding: 2, borderRadius: 4, minWidth: 20, cursor: "text" }}
														>
															{optionLabel}
														</span>
													) : (
														<span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
															{optionLabel}
														</span>
													)}
													{handleRemoveOption ? (
														<IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRemoveOption(i); }} style={{ padding: 4 }} title="Eliminar opción" disabled={opts.length <= 1}>
															<DeleteIcon style={{ fontSize: 18 }} />
														</IconButton>
													) : (
														<span style={{ width: 16, flexShrink: 0, textAlign: "right", fontSize: 14 }}>
															{(answered ? o.correct : !simulateQuiz && o.correct) && <span style={{ color: "#22c55e" }}>✓</span>}
															{answered && isSelected && !o.correct && <span style={{ color: "#ef4444" }}>✗</span>}
														</span>
													)}
												</>
											)}
										</div>
									);
								})}
							</div>
							);})()}
							{/* FIX: botón añadir opción solo para tipos NO imagen (en imagen no tiene sentido en el canvas) */}
							{handleAddOption && !isImageType && (
								<button
									type="button"
									onClick={(e) => { e.stopPropagation(); handleAddOption(); }}
									style={{
										display: "flex", alignItems: "center", gap: 6, padding: "8px 12px",
										border: "1px dashed #cbd5e1", borderRadius: 8, background: "#f8fafc",
										cursor: "pointer", fontSize: 13, color: "#64748b", alignSelf: "flex-start",
										flexShrink: 0,
									}}
								>
									<AddIcon style={{ fontSize: 18 }} /> Añadir opción
								</button>
							)}
							{/* FIX: para imagen tipo imagen, botón añadir fuera del flujo normal como overlay */}
							{handleAddOption && isImageType && (
								<div style={{ flexShrink: 0, paddingTop: 4 }}>
									<button
										type="button"
										onClick={(e) => { e.stopPropagation(); handleAddOption(); }}
										style={{
											display: "flex", alignItems: "center", gap: 4, padding: "4px 8px",
											border: "1px dashed #cbd5e1", borderRadius: 6, background: "rgba(248,250,252,0.95)",
											cursor: "pointer", fontSize: 11, color: "#64748b", width: "100%",
											justifyContent: "center",
										}}
									>
										<AddIcon style={{ fontSize: 14 }} /> Añadir opción
									</button>
								</div>
							)}
							{answered && firstQ.explanation && (
								<div style={{ fontSize: 11, color: "#64748b", fontStyle: "italic", marginTop: 4, padding: 6, background: "#f8fafc", borderRadius: 6, flexShrink: 0 }}>
									💡 {firstQ.explanation}
								</div>
							)}
							{answered && (
								<div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2, flexShrink: 0 }}>
									{answer.correct ? `+${quiz?.pointsCorrect ?? 10} pts` : `${quiz?.pointsIncorrect ?? -5} pts`}
								</div>
							)}
						</>
					) : (
						<div style={{ padding: 8, color: "#94a3b8", fontSize: 12, textAlign: "center", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
							{selected ? "Asigna un quiz en el panel" : "Quiz sin asignar"}
						</div>
					)}
				</div>
			</div>
		</>
		);
	}
	if (element.type === "shape") {
		const c = element.content || {};
		const fill = c.fillColor || "rgba(100, 100, 255, 0.8)";
		const stroke = c.strokeColor || "rgba(0, 0, 0, 1)";
		const strokeW = c.strokeWidth ?? 2;
		const shapeType = c.shapeType || "square";
		const radius = c.borderRadius ?? 0;
		const fillIsGrad = isShapeGradient(fill);
		const strokeIsGrad = isShapeGradient(stroke);

		const fillBg = fillIsGrad ? { background: fill } : { backgroundColor: fill };
		const strokeBorder = strokeIsGrad ? {} : { border: `${strokeW}px solid ${stroke}` };

		let innerStyle = {
			width: "100%",
			height: "100%",
			...fillBg,
			...strokeBorder,
			boxSizing: "border-box",
		};

		if (shapeType === "circle") {
			innerStyle = { ...innerStyle, borderRadius: "50%" };
		} else if (shapeType === "triangle") {
			innerStyle = null;
		} else if (shapeType === "line") {
			innerStyle = {
				width: "100%",
				height: Math.max(strokeW, 2),
				minHeight: Math.max(strokeW, 2),
				...(strokeIsGrad ? { background: stroke } : { backgroundColor: stroke }),
				border: "none",
				alignSelf: "center",
				borderRadius: radius ? `${Math.min(radius, 999)}px` : 0,
			};
		} else {
			innerStyle.borderRadius = radius ? `${Math.min(radius, 999)}px` : 0;
		}

		const needsStrokeWrapper = strokeIsGrad && strokeW > 0 && shapeType !== "line";
		const innerRadius = shapeType === "circle" ? "50%" : (radius ? `${Math.min(radius, 999)}px` : 0);
		const wrapperStyle = needsStrokeWrapper ? {
			width: "100%", height: "100%", padding: strokeW, background: stroke, borderRadius: shapeType === "circle" ? "50%" : (radius + strokeW), boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center",
		} : null;
		const wrappedInnerStyle = needsStrokeWrapper ? {
			flex: 1, minWidth: 0, minHeight: 0, ...fillBg, borderRadius: innerRadius, boxSizing: "border-box",
		} : innerStyle;

		return (
			<div
				style={{
					...style,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
				onMouseDown={handleMouseDown}
				onClick={(e) => { e.stopPropagation(); if (!locked) onSelect(element); }}
			>
				<ResizeHandles />
				{shapeType === "triangle" ? (
					<svg
						width="100%"
						height="100%"
						viewBox="0 0 100 100"
						preserveAspectRatio="none"
						style={{ overflow: "visible" }}
					>
						{fillIsGrad && (
							<defs>
								<linearGradient id={`fill-grad-${element.elementId}`} x1="0%" y1="0%" x2="100%" y2="100%">
									{(() => {
										const m = fill.match(/linear-gradient\s*\(\s*\d+\s*deg\s*,\s*(.+?)\s*\d+%\s*,\s*(.+?)\s*\d+%\s*\)/);
										if (m) return [<stop key="1" offset="0%" stopColor={m[1].trim()} />, <stop key="2" offset="100%" stopColor={m[2].trim()} />];
										return null;
									})()}
								</linearGradient>
							</defs>
						)}
						{strokeIsGrad && (
							<defs>
								<linearGradient id={`stroke-grad-${element.elementId}`} x1="0%" y1="0%" x2="100%" y2="100%">
									{(() => {
										const m = stroke.match(/linear-gradient\s*\(\s*\d+\s*deg\s*,\s*(.+?)\s*\d+%\s*,\s*(.+?)\s*\d+%\s*\)/);
										if (m) return [<stop key="1" offset="0%" stopColor={m[1].trim()} />, <stop key="2" offset="100%" stopColor={m[2].trim()} />];
										return null;
									})()}
								</linearGradient>
							</defs>
						)}
						<polygon
							points="50,5 95,95 5,95"
							fill={fillIsGrad ? `url(#fill-grad-${element.elementId})` : fill}
							stroke={strokeIsGrad ? `url(#stroke-grad-${element.elementId})` : stroke}
							strokeWidth={strokeW}
							strokeLinejoin={radius > 0 ? "round" : "miter"}
							vectorEffect="non-scaling-stroke"
						/>
					</svg>
				) : needsStrokeWrapper ? (
					<div style={wrapperStyle}>
						<div style={wrappedInnerStyle} />
					</div>
				) : (
					<div style={innerStyle} />
				)}
			</div>
		);
	}
	if (element.type === "hint") {
		const c = element.content || {};
		const label = c.label || "Pista";
		const hintText = c.hintText || "";
		const hintPenalty = c.hintPenalty ?? 0;
		const s = element.style || {};
		const borderW = Math.max(0, s.borderWidth ?? 1);
		const borderColor = s.borderColor || "rgba(124, 58, 237, 1)";
		const btnStyle = {
			width: "100%",
			height: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			backgroundColor: s.backgroundColor || "#f5f3ff",
			color: s.color || "#7c3aed",
			fontSize: s.fontSize || 14,
			fontWeight: s.fontWeight || "bold",
			fontFamily: s.fontFamily || "Arial, sans-serif",
			borderRadius: (s.borderRadius ?? 8) >= 0 ? s.borderRadius : 8,
			border: borderW > 0 ? `${borderW}px solid ${borderColor}` : "none",
			cursor: "pointer",
		};
		const handleHintClick = (e) => {
			e.stopPropagation();
			if (preview) {
				// En thumbnails: usar callback del padre para que el modal se renderice fuera
				if (hintText.trim() && onHintPreviewClick) {
					onHintPreviewClick(hintText);
				} else if (hintText.trim()) {
					setHintModal({ type: "hint", text: hintText });
				}
				return;
			}
			if (locked && !simulateQuiz) return; // En play mode (simulateQuiz) permitir el modal
			if (!simulateQuiz) { onSelect(element); return; }
			if (!hintText.trim()) return;
			const payload = hintPenalty > 0 ? { type: "confirm", penalty: hintPenalty, text: hintText, label, elementId: element.elementId } : { type: "hint", text: hintText };
			if (onHintPlayModal) {
				onHintPlayModal(payload);
			} else {
				if (hintPenalty > 0) setHintModal({ type: "confirm", penalty: hintPenalty, text: hintText, label });
				else setHintModal({ type: "hint", text: hintText });
			}
		};
		const handleConfirmAccept = () => {
			setHintModal({ type: "hint", text: hintModal.text });
			if (onQuizHintUsed && hintPenalty > 0) onQuizHintUsed(element.elementId, hintPenalty);
		};
		return (
			<>
				<div
					{...(preview && hintText.trim() && { "data-hint-preview": "1", "data-hint-text": hintText })}
					style={{ ...style, ...((preview || simulateQuiz) && { cursor: "pointer" }), ...(preview && hintText.trim() && { pointerEvents: "auto" }) }}
					onMouseDown={handleMouseDown}
					onClick={handleHintClick}
				>
					<ResizeHandles />
					<div style={{ ...btnStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
						<LiveHelpIcon style={{ fontSize: (s.fontSize || 14) * 1.2 }} />
						{label}
					</div>
				</div>
				{!onHintPlayModal && hintModal?.type === "confirm" && hintModal.penalty === hintPenalty && (
					<Dialog open onClose={() => setHintModal(null)} ModalProps={{ style: { zIndex: 10001 }, BackdropProps: { style: { zIndex: 10001 } } }} PaperProps={{ style: { zIndex: 10001 } }}>
						<DialogTitle>Pista</DialogTitle>
						<DialogContent>Vas a perder {hintModal.penalty} puntos. ¿Estás seguro?</DialogContent>
						<DialogActions>
							<Button onClick={() => setHintModal(null)}>Cancelar</Button>
							<Button onClick={handleConfirmAccept} variant="contained" color="primary">Aceptar</Button>
						</DialogActions>
					</Dialog>
				)}
				{!onHintPlayModal && hintModal?.type === "hint" && (
					<Dialog open onClose={() => setHintModal(null)} ModalProps={{ style: { zIndex: 10001 }, BackdropProps: { style: { zIndex: 10001 } } }} PaperProps={{ style: { zIndex: 10001 } }}>
						<DialogTitle>Pista</DialogTitle>
						<DialogContent><div style={{ whiteSpace: "pre-wrap" }}>{hintModal.text}</div></DialogContent>
						<DialogActions>
							<Button onClick={() => setHintModal(null)} variant="contained" color="primary">Cerrar</Button>
						</DialogActions>
					</Dialog>
				)}
			</>
		);
	}
	if (element.type === "button") {
		const s = element.style || {};
		const label = element.content?.label || "";
		const icon = element.content?.icon || "";
		const bg = s.backgroundColor || "transparent";
		const hasBg = bg && bg !== "transparent";
		const radius = typeof s.borderRadius === "number" ? s.borderRadius : 8;
		const btnStyle = {
			width: "100%",
			height: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			gap: 6,
			backgroundColor: bg,
			color: s.color || "rgba(51, 51, 51, 1)",
			fontSize: s.fontSize || 16,
			fontWeight: s.fontWeight || "bold",
			fontFamily: s.fontFamily || "Arial, sans-serif",
			borderRadius: radius >= 0 ? radius : 8,
			border: hasBg ? "none" : "1px dashed rgba(0,0,0,0.2)",
			boxShadow: hasBg ? "0 2px 6px rgba(0,0,0,0.12)" : "none",
			cursor: preview ? "default" : "pointer",
		};
		const showLabel = String(label).trim();
		const showIcon = String(icon || "").trim();
		const displayText = !showLabel && !showIcon ? "Clic aquí" : null;
		const iconPos = element.content?.iconPosition || "before";
		const iconScale = element.content?.iconSize ?? 1.2;
		const iconFontSize = (s.fontSize || 16) * iconScale;
		const iconSpan = showIcon ? (icon === "arrowRight" ? <ArrowForwardIcon style={{ fontSize: iconFontSize }} /> : <span style={{ fontSize: iconFontSize }}>{icon}</span>) : null;
		const textSpan = (showLabel || displayText) ? <span>{showLabel || displayText}</span> : null;
		const handleClick = (e) => {
			e.stopPropagation();
			if (onButtonAction && element.content?.action) {
				onButtonAction(element);
				return;
			}
			if (locked) return;
			onSelect(element);
		};
		return (
			<div
				style={style}
				onMouseDown={handleMouseDown}
				onClick={handleClick}
			>
				<ResizeHandles />
				<div style={btnStyle}>
					{iconPos === "before" ? iconSpan : textSpan}
					{iconPos === "before" ? textSpan : iconSpan}
				</div>
			</div>
		);
	}
	return null;
}

const THUMB_WIDTH = 128;
const THUMB_HEIGHT = 72;

function PagePreviewThumbnail({ elements, registrationDesign, isRegistration, backgroundColor, backgroundImageUrl, backgroundImageOpacity, quizzes = [] }) {
	const scale = THUMB_WIDTH / CANVAS_WIDTH;
	const noop = () => {};
	const [hintModalText, setHintModalText] = useState(null);
	if (isRegistration) {
		return (
			<div
				style={{
					width: THUMB_WIDTH,
					height: THUMB_HEIGHT,
					overflow: "hidden",
					borderRadius: 4,
					boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
					position: "relative",
					backgroundColor: registrationDesign?.backgroundColor || "#fff",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: 2,
						padding: "6px 12px",
						background: "rgba(255,255,255,0.95)",
						borderRadius: 6,
						boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
						border: "1px solid rgba(0,0,0,0.04)",
					}}
				>
					<span
						style={{
							fontSize: 10,
							fontWeight: 700,
							color: "#1a1a1a",
							letterSpacing: "0.12em",
							textTransform: "uppercase",
						}}
					>
						Registro
					</span>
				</div>
			</div>
		);
	}
	return (
		<div
			style={{
				width: THUMB_WIDTH,
				height: THUMB_HEIGHT,
				overflow: "hidden",
				backgroundColor: backgroundColor || "#fff",
				borderRadius: 4,
				boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
				position: "relative",
			}}
		>
			{backgroundImageUrl && (
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundImage: `url(${backgroundImageUrl})`,
						backgroundSize: "cover",
						backgroundPosition: "center",
						opacity: backgroundImageOpacity != null ? backgroundImageOpacity / 100 : 1,
						pointerEvents: "none",
					}}
				/>
			)}
			<div
				style={{
					width: CANVAS_WIDTH,
					height: CANVAS_HEIGHT,
					transform: `scale(${scale})`,
					transformOrigin: "0 0",
					position: "relative",
					zIndex: 1,
					pointerEvents: "none",
				}}
				onClick={(e) => {
					const hintEl = e.target.closest("[data-hint-preview]");
					if (hintEl && hintEl.dataset.hintText) {
						e.stopPropagation();
						setHintModalText(hintEl.dataset.hintText);
					}
				}}
			>
				{(elements || []).map((el) => (
					<ElementRenderer
						key={el.elementId}
						element={el}
						selected={false}
						preview
						onSelect={noop}
						onUpdate={noop}
						onDragStart={noop}
						onResizeStart={noop}
						registrationDesign={undefined}
						quizData={el.type === "quiz" ? quizzes.find((q) => q._id === el.content?.quizId) : undefined}
						onButtonAction={noop}
						onQuizHintUsed={noop}
						onHintPreviewClick={(text) => setHintModalText(text)}
					/>
				))}
			</div>
			{hintModalText && (
				<Dialog open onClose={() => setHintModalText(null)} style={{ zIndex: 9999 }}>
					<DialogTitle>Pista</DialogTitle>
					<DialogContent><div style={{ whiteSpace: "pre-wrap" }}>{hintModalText}</div></DialogContent>
					<DialogActions>
						<Button onClick={() => setHintModalText(null)} variant="contained" color="primary">Cerrar</Button>
					</DialogActions>
				</Dialog>
			)}
		</div>
	);
}

export const REGISTRATION_PAGE_ID = "registration";

const PRIMARY_RED = "#EB1700";
const PRIMARY_RGBA = "rgba(235, 23, 0, 1)";

export const getDefaultRegistrationPage = () => {
	const el = (html, x, y, w, h, style = {}, locked = true) => ({
		...defaultElement("text"),
		x, y, width: w, height: h,
		content: { html: `<p>${html}</p>` },
		style: { fontFamily: "Helvetica, Arial, sans-serif", fontSize: 18, color: PRIMARY_RED, fontWeight: "bold", ...style },
		registrationLocked: locked,
	});
	const shape = (type, x, y, w, h, content = {}, locked = true) => ({
		...defaultElement(`shape-${type}`),
		x, y, width: w, height: h,
		content: { shapeType: type, fillColor: "rgba(235, 23, 0, 0.15)", strokeColor: PRIMARY_RGBA, strokeWidth: 2, borderRadius: 0, ...content },
		registrationLocked: locked,
	});
	const img = (x, y, w, h, role, url = "") => ({
		...defaultElement("image"),
		x, y, width: w, height: h,
		content: { url, alt: role === "logo" ? "Logo" : "" },
		registrationLocked: false,
		registrationRole: role,
	});

	return {
		pageId: REGISTRATION_PAGE_ID,
		order: 0,
		name: "Registro",
		registrationDesign: {
			backgroundImageUrl: "",
			backgroundColor: "#ffffff",
			logoUrl: "",
			primaryColor: PRIMARY_RED,
		},
		elements: [
			img(430, 8, 100, 100, "logo"),
			el("Haz una foto de equipo", 25, 55, 220, 28, { fontSize: 22, backgroundColor: "rgba(255,255,255,0.7)", color: PRIMARY_RED }, true),
			shape("circle", 50, 88, 140, 140, { fillColor: "rgba(255,255,255,0.85)", strokeColor: PRIMARY_RGBA, strokeWidth: 2 }, true),
			el("📷", 95, 130, 50, 50, { fontSize: 40, textAlign: "center" }, true),
			el("Añade los participantes", 480, 55, 250, 28, { fontSize: 22 }, true),
			shape("square", 480, 88, 360, 38, { fillColor: "transparent", strokeColor: PRIMARY_RGBA, strokeWidth: 2 }, true),
			el("Escribe nombre y apellido", 495, 96, 280, 22, { fontSize: 14, fontWeight: "normal", color: "#999" }, true),
			shape("circle", 830, 93, 38, 38, { fillColor: PRIMARY_RGBA }, true),
			el("+", 838, 88, 22, 38, { fontSize: 28, color: "#fff", textAlign: "center" }, true),
			el("Participantes del grupo", 480, 138, 250, 24, { fontSize: 20 }, true),
			el("Debe añadir algún participante al grupo", 480, 165, 350, 22, { fontSize: 14, fontWeight: "normal" }, true),
			el("Elige tu número de mesa asignado", 25, 248, 320, 26, { fontSize: 20 }, true),
			el("Tu mesa tiene un número asignado, debes seleccionar ese número para el registro", 25, 276, 400, 36, { fontSize: 12, fontWeight: "normal" }, true),
			shape("square", 25, 315, 260, 38, { fillColor: "transparent", strokeColor: PRIMARY_RGBA, strokeWidth: 2 }, true),
			el("Selecciona una mesa", 40, 322, 230, 24, { fontSize: 14, fontWeight: "normal", color: "#999" }, true),
			el("Pon un nombre a tu equipo", 25, 368, 260, 26, { fontSize: 20 }, true),
			el("(Máximo 15 caracteres)", 25, 396, 180, 20, { fontSize: 12, fontWeight: "normal" }, true),
			shape("square", 25, 420, 260, 38, { fillColor: "transparent", strokeColor: PRIMARY_RGBA, strokeWidth: 2 }, true),
			el("Escribe el nombre", 40, 427, 230, 24, { fontSize: 14, fontWeight: "normal", color: "#999" }, true),
			el("Siguiente", 700, 455, 90, 28, { fontSize: 22 }, true),
			shape("circle", 800, 435, 70, 70, { fillColor: PRIMARY_RGBA }, true),
			el("→", 818, 442, 35, 36, { fontSize: 32, color: "#fff", textAlign: "center" }, true),
		],
	};
};

const pageHasQuizOrPoints = (page) =>
	(page?.elements || []).some((e) => e.type === "quiz");

const normalizePages = (pages) => {
	const registration = getDefaultRegistrationPage();
	if (!pages || !Array.isArray(pages) || pages.length === 0) {
		return [registration];
	}
	const hasRegistration = pages.some((p) => (p.pageId || p._id) === REGISTRATION_PAGE_ID);
	let normalized = pages.map((p, i) => ({
		pageId: p.pageId || p._id || genId(),
		order: p.order ?? i,
		name: p.name || `Página ${i + 1}`,
		backgroundColor: p.backgroundColor,
		backgroundImageUrl: p.backgroundImageUrl,
		backgroundImageOpacity: p.backgroundImageOpacity,
		transitionToNext: p.transitionToNext || "none",
		transitionDuration: p.transitionDuration ?? 350,
		registrationDesign: (p.pageId || p._id) === REGISTRATION_PAGE_ID
			? (() => {
				const rd = { ...registration.registrationDesign, ...(p.registrationDesign || {}) };
				if (!rd.logoUrl && (rd.logoOncommitUrl || rd.logoJandJUrl)) {
					rd.logoUrl = rd.logoOncommitUrl || rd.logoJandJUrl;
				}
				delete rd.logoOncommitUrl;
				delete rd.logoJandJUrl;
				return rd;
			})()
			: undefined,
		elements: (p.elements || []).map((e) => ({
			...e,
			elementId: e.elementId || e._id || genId(),
		})),
	}));
	if (!hasRegistration) {
		normalized = [registration, ...normalized.map((p, i) => ({ ...p, order: i + 1 }))];
	} else {
		const regPage = normalized.find((p) => (p.pageId || p._id) === REGISTRATION_PAGE_ID);
		const defaultReg = getDefaultRegistrationPage();
		const mergedReg = {
			...defaultReg,
			registrationDesign: { ...defaultReg.registrationDesign, ...(regPage.registrationDesign || {}) },
		};
		const oldLogoRoles = ["logoOncommit", "logoJandJ"];
		const hasOldLogos = (mergedReg.elements || []).some((e) => oldLogoRoles.includes(e.registrationRole));
		const hasNewLogo = (mergedReg.elements || []).some((e) => e.registrationRole === "logo");
		if (hasOldLogos && !hasNewLogo) {
			const logoEl = {
				...defaultElement("image"),
				elementId: genId(),
				x: 430, y: 8, width: 100, height: 100,
				content: { url: "", alt: "Logo" },
				registrationLocked: false,
				registrationRole: "logo",
			};
			mergedReg.elements = (mergedReg.elements || []).filter((e) => !oldLogoRoles.includes(e.registrationRole));
			mergedReg.elements.unshift(logoEl);
		}
		normalized = normalized.map((p) =>
			(p.pageId || p._id) === REGISTRATION_PAGE_ID ? mergedReg : p
		);
	}
	return normalized.map((p, i) => ({ ...p, order: i }));
};

function PlayModeView({
	playModeContainerRef,
	pages,
	playPageIndex,
	playViewport,
	playViewportDim,
	playScale,
	setPlayScale,
	setPlayMode,
	setPlayViewport,
	playTransitionKey,
	playTransitionType,
	playTransitionDuration,
	setPlayTransitionType,
	setPlayTransitionDuration,
	setPlayTransitionKey,
	setPlayPageIndex,
	playPage,
	playElements,
	isPlayRegPage,
	bgImgResolved,
	handlePlayButtonAction,
	handlePlayPrev,
	handlePlayNext,
	handleExitPlay,
	handleQuizOptionSelect,
	handleQuizHintUsed,
	setPlayHintModal,
	playHintModal,
	playQuizAnswers,
	playModalContent,
	setPlayModalContent,
	quizzes,
}) {
	const updatePlayScale = useCallback(() => {
		const container = playModeContainerRef.current;
		if (!container) return;
		const rect = container.getBoundingClientRect();
		const navBarH = 72;
		const pad = 32;
		const availW = Math.max(100, rect.width - pad);
		const availH = Math.max(100, rect.height - navBarH - pad);
		const scale = Math.min(availW / playViewportDim.width, availH / playViewportDim.height, 3);
		setPlayScale(Math.max(0.5, scale));
	}, [playModeContainerRef, playViewportDim.width, playViewportDim.height, setPlayScale]);

	useEffect(() => {
		const el = playModeContainerRef.current;
		if (!el) return;
		const enterFullscreen = () => {
			if (el.requestFullscreen) el.requestFullscreen();
			else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
			else if (el.msRequestFullscreen) el.msRequestFullscreen();
		};
		enterFullscreen();
		const onFullscreenChange = () => {
			if (!document.fullscreenElement && !document.webkitFullscreenElement) setPlayMode(false);
			else setTimeout(updatePlayScale, 100);
		};
		document.addEventListener("fullscreenchange", onFullscreenChange);
		document.addEventListener("webkitfullscreenchange", onFullscreenChange);
		setTimeout(updatePlayScale, 150);
		return () => {
			document.removeEventListener("fullscreenchange", onFullscreenChange);
			document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
			if (document.exitFullscreen) document.exitFullscreen();
			else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
		};
	}, [playModeContainerRef, setPlayMode, updatePlayScale]);

	useEffect(() => {
		updatePlayScale();
		const ro = playModeContainerRef.current && new ResizeObserver(updatePlayScale);
		if (ro && playModeContainerRef.current) ro.observe(playModeContainerRef.current);
		return () => ro?.disconnect();
	}, [playModeContainerRef, playPageIndex, playViewport, updatePlayScale]);

	return (
		<Box
			ref={playModeContainerRef}
			position="fixed"
			top={0}
			left={0}
			right={0}
			bottom={0}
			zIndex={9999}
			display="flex"
			flexDirection="column"
			bgcolor="#0f172a"
		>
			<Box position="absolute" top={12} left={12} right={12} zIndex={10} display="flex" alignItems="center" justifyContent="space-between">
				<Box display="flex" alignItems="center" style={{ gap: 6 }}>
					{VIEWPORTS.map((vp) => {
						const Icon = vp.Icon;
						const active = playViewport === vp.id;
						return (
							<Tooltip key={vp.id} title={vp.label}>
								<IconButton
									onClick={() => setPlayViewport(vp.id)}
									style={{
										background: active ? "rgba(124,58,237,0.9)" : "rgba(255,255,255,0.9)",
										color: active ? "#fff" : "#334155",
									}}
									size="small"
								>
									<Icon style={{ fontSize: 20 }} />
								</IconButton>
							</Tooltip>
						);
					})}
					<Box fontSize={12} color="#94a3b8" ml={1}>
						{VIEWPORTS.find((v) => v.id === playViewport)?.label}
					</Box>
				</Box>
				<IconButton onClick={handleExitPlay} style={{ background: "rgba(255,255,255,0.9)", color: "#334155" }} title="Salir (Esc)">
					<CloseIcon />
				</IconButton>
			</Box>
			<style>{`
				@keyframes er-play-fade { from { opacity: 0; } to { opacity: 1; } }
				@keyframes er-play-slideLeft { from { opacity: 0; transform: translateX(20%); } to { opacity: 1; transform: translateX(0); } }
				@keyframes er-play-slideRight { from { opacity: 0; transform: translateX(-20%); } to { opacity: 1; transform: translateX(0); } }
				@keyframes er-play-slideUp { from { opacity: 0; transform: translateY(20%); } to { opacity: 1; transform: translateY(0); } }
				@keyframes er-play-slideDown { from { opacity: 0; transform: translateY(-20%); } to { opacity: 1; transform: translateY(0); } }
				@keyframes er-play-zoom { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
			`}</style>
			<Box flex={1} display="flex" alignItems="center" justifyContent="center" p={2} overflow="hidden">
				<Box
					key={playTransitionKey}
					position="relative"
					style={playTransitionType !== "none" ? { animation: `er-play-${playTransitionType} ${(playTransitionDuration || 350) / 1000}s ease-out forwards` } : {}}
				>
					<Box
						position="relative"
						width={playViewportDim.width}
						height={playViewportDim.height}
						style={{
							backgroundColor: isPlayRegPage ? (playPage?.registrationDesign?.backgroundColor || "#fff") : (playPage?.backgroundColor || "#fff"),
							boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
							borderRadius: 8,
							overflow: "hidden",
							transform: `scale(${playScale})`,
							transformOrigin: "center center",
						}}
					>
						{!isPlayRegPage && bgImgResolved && (
							<div
								style={{
									position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
									backgroundImage: `url(${bgImgResolved})`,
									backgroundSize: "cover",
									backgroundPosition: "center",
									opacity: playPage?.backgroundImageOpacity != null ? playPage.backgroundImageOpacity / 100 : 1,
									pointerEvents: "none",
								}}
							/>
						)}
						{isPlayRegPage ? (
							<Box display="flex" alignItems="center" justifyContent="center" height="100%" flexDirection="column" gap={2} p={4}>
								<AssignmentIcon style={{ fontSize: 48, color: "#EB1700" }} />
								<Box fontSize={24} fontWeight={700} color="#1a1a1a">Página de Registro</Box>
								<Box fontSize={14} color="#666">(Vista previa del diseño)</Box>
							</Box>
						) : (
							playElements.map((el) => (
								<ElementRenderer
									key={el.elementId}
									element={el}
									selected={false}
									onSelect={() => {}}
									onUpdate={() => {}}
									onDragStart={() => {}}
									onResizeStart={() => {}}
									preview={false}
									isLocked
									registrationDesign={playPage?.registrationDesign}
									onButtonAction={handlePlayButtonAction}
									quizData={el.type === "quiz" ? quizzes.find((q) => q._id === el.content?.quizId) : undefined}
									simulateQuiz
									playQuizAnswer={playQuizAnswers}
									onQuizOptionSelect={handleQuizOptionSelect}
									onQuizHintUsed={handleQuizHintUsed}
									onHintPlayModal={setPlayHintModal}
								/>
							))
						)}
					</Box>
				</Box>
			</Box>
			<Box display="flex" alignItems="center" justifyContent="space-between" px={4} py={2} style={{ background: "rgba(15,23,42,0.95)" }}>
				<Button disabled={playPageIndex <= 0} onClick={handlePlayPrev} style={{ color: "#fff", textTransform: "none" }}>
					← Anterior
				</Button>
				<Box fontSize={14} color="#94a3b8">
					Página {playPageIndex + 1} de {pages.length}
				</Box>
				<Button disabled={playPageIndex >= pages.length - 1} onClick={handlePlayNext} style={{ color: "#fff", textTransform: "none" }}>
					Siguiente →
				</Button>
			</Box>
			{playHintModal?.type === "confirm" && (
				<Dialog open onClose={() => setPlayHintModal(null)} ModalProps={{ style: { zIndex: 10001 }, BackdropProps: { style: { zIndex: 10001 } } }} PaperProps={{ style: { zIndex: 10001 } }}>
					<DialogTitle>Pista</DialogTitle>
					<DialogContent>Vas a perder {playHintModal.penalty} puntos. ¿Estás seguro?</DialogContent>
					<DialogActions>
						<Button onClick={() => setPlayHintModal(null)}>Cancelar</Button>
						<Button
							onClick={() => {
								setPlayHintModal({ type: "hint", text: playHintModal.text });
								handleQuizHintUsed(playHintModal.elementId, playHintModal.penalty);
							}}
							variant="contained"
							color="primary"
						>
							Aceptar
						</Button>
					</DialogActions>
				</Dialog>
			)}
			{playHintModal?.type === "hint" && (
				<Dialog open onClose={() => setPlayHintModal(null)} ModalProps={{ style: { zIndex: 10001 }, BackdropProps: { style: { zIndex: 10001 } } }} PaperProps={{ style: { zIndex: 10001 } }}>
					<DialogTitle>Pista</DialogTitle>
					<DialogContent><div style={{ whiteSpace: "pre-wrap" }}>{playHintModal.text}</div></DialogContent>
					<DialogActions>
						<Button onClick={() => setPlayHintModal(null)} variant="contained" color="primary">Cerrar</Button>
					</DialogActions>
				</Dialog>
			)}
			{playModalContent && (
				<Box
					position="fixed"
					top={0}
					left={0}
					right={0}
					bottom={0}
					zIndex={10002}
					display="flex"
					alignItems="center"
					justifyContent="center"
					onClick={(e) => e.target === e.currentTarget && setPlayModalContent(null)}
					style={{ background: "rgba(0,0,0,0.6)" }}
				>
					<Box
						position="relative"
						width={playModalContent.width}
						height={playModalContent.height}
						style={{
							backgroundColor: playModalContent.design?.backgroundColor || "#ffffff",
							borderRadius: playModalContent.design?.borderRadius ?? 12,
							boxShadow: "0 12px 48px rgba(0,0,0,0.4)",
							overflow: "hidden",
						}}
						onClick={(e) => e.stopPropagation()}
					>
						{(playModalContent.design?.elements || []).map((el) => (
							<ElementRenderer
								key={el.elementId}
								element={el}
								selected={false}
								onSelect={() => {}}
								onUpdate={() => {}}
								onDragStart={() => {}}
								onResizeStart={() => {}}
								preview
								isLocked
								onButtonAction={() => {}}
								quizData={el.type === "quiz" ? quizzes.find((q) => q._id === el.content?.quizId) : undefined}
								simulateQuiz
								playQuizAnswer={{}}
								onQuizOptionSelect={() => {}}
								onQuizHintUsed={() => {}}
							/>
						))}
						<IconButton
							onClick={() => setPlayModalContent(null)}
							style={{ position: "absolute", top: 8, right: 8, background: "rgba(255,255,255,0.9)", zIndex: 10 }}
							size="small"
							title="Cerrar"
						>
							<CloseIcon />
						</IconButton>
					</Box>
				</Box>
			)}
		</Box>
	);
}

export default function EscapeRoomEditor({
	escapeRoom,
	onSave,
	onDelete,
	saving,
}) {
	const [pages, setPages] = useState(() => normalizePages(escapeRoom.pages));
	const [currentPageIndex, setCurrentPageIndex] = useState(0);
	const [selectedElement, setSelectedElement] = useState(null);
	const [name, setName] = useState(escapeRoom.name || "");
	const [addPanelCategory, setAddPanelCategory] = useState("elementos");
	const [activeViewport, setActiveViewport] = useState("desktop");
	const [pageMenuAnchor, setPageMenuAnchor] = useState(null);
	const [pageMenuIdx, setPageMenuIdx] = useState(null);
	const [canvasZoom, setCanvasZoom] = useState(1);
	const [canvasPan, setCanvasPan] = useState({ x: 0, y: 0 });
	const [plantillas, setPlantillas] = useState([]);
	const [plantillasLoading, setPlantillasLoading] = useState(false);
	const [quizzes, setQuizzes] = useState([]);
	const [quizzesLoading, setQuizzesLoading] = useState(false);
	const [userMedia, setUserMedia] = useState([]);
	const [userMediaLoading, setUserMediaLoading] = useState(false);
	const [backgroundPickerOpen, setBackgroundPickerOpen] = useState(false);
	const [logoPickerOpen, setLogoPickerOpen] = useState(false);
	const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
	const [mediaPickerMode, setMediaPickerMode] = useState(null);
	const [playMode, setPlayMode] = useState(false);
	const [playHintModal, setPlayHintModal] = useState(null);
	const [playQuizAnswers, setPlayQuizAnswers] = useState({});
	const [playPageIndex, setPlayPageIndex] = useState(0);
	const [playTransitionKey, setPlayTransitionKey] = useState(0);
	const [playTransitionType, setPlayTransitionType] = useState("none");
	const [playTransitionDuration, setPlayTransitionDuration] = useState(350);
	const [playViewport, setPlayViewport] = useState("desktop");
	const [playScale, setPlayScale] = useState(1);
	const [playModalContent, setPlayModalContent] = useState(null);
	const [editingModalButton, setEditingModalButton] = useState(null);
	const [modalCanvasZoom, setModalCanvasZoom] = useState(1);
	const [modalCanvasPan, setModalCanvasPan] = useState({ x: 0, y: 0 });
	const playModeContainerRef = useRef(null);
	const fileInputRef = useRef(null);
	const backgroundFileInputRef = useRef(null);
	const logoFileInputRef = useRef(null);
	const mediaElementFileInputRef = useRef(null);
	const canvasViewportRef = useRef(null);
	const canvasDropRef = useRef(null);
	const isFirstRender = useRef(true);
	const autoSaveTimeoutRef = useRef(null);
	const onSaveRef = useRef(onSave);
	onSaveRef.current = onSave;

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
		autoSaveTimeoutRef.current = setTimeout(() => {
			autoSaveTimeoutRef.current = null;
			onSaveRef.current({ name: name.trim(), pages }, { silent: true });
		}, 1500);
		return () => {
			if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
		};
	}, [name, pages]);

	useEffect(() => {
		if (addPanelCategory === "plantillas" && plantillas.length === 0) {
			setPlantillasLoading(true);
			getPlantillas()
				.then((res) => setPlantillas(res.data || []))
				.catch(() => setPlantillas([]))
				.finally(() => setPlantillasLoading(false));
		}
	}, [addPanelCategory, plantillas.length]);

	const loadQuizzes = useCallback(() => {
		setQuizzesLoading(true);
		getQuizzes()
			.then((res) => setQuizzes(res.data || []))
			.catch(() => setQuizzes([]))
			.finally(() => setQuizzesLoading(false));
	}, []);
	useEffect(() => {
		if (addPanelCategory === "quizzes") loadQuizzes();
	}, [addPanelCategory, loadQuizzes]);
	useEffect(() => {
		if (selectedElement?.type === "quiz" && quizzes.length === 0) loadQuizzes();
	}, [selectedElement?.type, quizzes.length, loadQuizzes]);
	useEffect(() => {
		const hasQuiz = (pages || []).some((p) => (p.elements || []).some((e) => e.type === "quiz"));
		if (hasQuiz && quizzes.length === 0) loadQuizzes();
	}, [pages, quizzes.length, loadQuizzes]);

	const loadUserMedia = useCallback(() => {
		setUserMediaLoading(true);
		getUserMedia()
			.then((res) => setUserMedia(res.data?.items || []))
			.catch(() => setUserMedia([]))
			.finally(() => setUserMediaLoading(false));
	}, []);

	useEffect(() => {
		if (addPanelCategory === "multimedia" || backgroundPickerOpen || logoPickerOpen || mediaPickerOpen || selectedElement?.type === "quiz") {
			loadUserMedia();
		}
	}, [addPanelCategory, backgroundPickerOpen, logoPickerOpen, mediaPickerOpen, selectedElement?.type, loadUserMedia]);

	const handleUploadUserMedia = useCallback((e) => {
		const file = e?.target?.files?.[0];
		if (!file) return;
		const isImg = /\.(jpe?g|png|gif|webp|svg)$/i.test(file.name);
		const isVid = /\.(mp4|webm|ogg|mov)$/i.test(file.name);
		if (!isImg && !isVid) {
			alertError({ customMessage: "Solo se permiten imágenes (jpg, png, gif, webp, svg) o videos (mp4, webm, ogg, mov)." });
			return;
		}
		uploadUserMedia(file)
			.then((res) => {
				if (res?.data?.fileURL) {
					setUserMedia((prev) => [{ path: res.data.fileURL, name: file.name, isVideo: isVid }, ...prev]);
					return res.data.fileURL;
				}
			})
			.catch((err) => {
				alertError({ error: err, customMessage: "Error al subir el archivo." });
			});
	}, []);

	const ADD_CATEGORIES = [
		{ id: "editar", label: "Editar", shortLabel: "Editar", Icon: EditIcon },
		{ id: "plantillas", label: "Plantillas", shortLabel: "Plantillas", Icon: ViewModuleIcon },
		{ id: "elementos", label: "Elementos", shortLabel: "Formas", Icon: CategoryIcon },
		{ id: "texto", label: "Texto", shortLabel: "Texto", Icon: TitleIcon },
		{ id: "quizzes", label: "Preguntas", shortLabel: "Preg.", Icon: LiveHelpIcon },
		{ id: "interactivos", label: "Elementos Interactivos", shortLabel: "Interact.", Icon: TouchAppIcon },
		{ id: "multimedia", label: "Multimedia", shortLabel: "Media", Icon: PermMediaIcon },
	];
	const REGISTRATION_CATEGORIES = [
		{ id: "editar", label: "Editar", shortLabel: "Editar", Icon: EditIcon },
		{ id: "configuracion", label: "Configuración", shortLabel: "Config", Icon: SettingsIcon },
		{ id: "diseño", label: "Diseño de registro", shortLabel: "Diseño", Icon: PaletteIcon },
		{ id: "elementos", label: "Elementos", shortLabel: "Formas", Icon: CategoryIcon },
		{ id: "texto", label: "Texto", shortLabel: "Texto", Icon: TitleIcon },
	];

	const currentPage = pages[currentPageIndex] || pages[0];
	const elements = getElementsForViewport(currentPage, activeViewport);
	const viewportDim = VIEWPORTS.find((v) => v.id === activeViewport) || VIEWPORTS[0];
	const isRegistrationPage = (currentPage?.pageId || currentPage?._id) === REGISTRATION_PAGE_ID;

	useEffect(() => {
		if (selectedElement) {
			setAddPanelCategory("editar");
		}
	}, [selectedElement?.elementId]);

	useEffect(() => {
		if (isRegistrationPage && !selectedElement) {
			setAddPanelCategory("diseño");
		}
	}, [isRegistrationPage, currentPageIndex, selectedElement]);

	const addPage = useCallback(() => {
		const newPage = {
			pageId: genId(),
			order: pages.length,
			name: `Página ${pages.length + 1}`,
			elements: [],
			transitionToNext: "none",
			transitionDuration: 350,
		};
		setPages((p) => [...p, newPage]);
		setCurrentPageIndex(pages.length);
		setSelectedElement(null);
	}, [pages.length]);

	const removePage = useCallback((idx) => {
		const page = pages[idx];
		if (!page || (page.pageId || page._id) === REGISTRATION_PAGE_ID) return;
		if (pages.length <= 1) return;
		setPages((p) => p.filter((_, i) => i !== idx));
		setCurrentPageIndex((i) => (i >= idx && i > 0 ? i - 1 : i));
		setSelectedElement(null);
	}, [pages.length]);

	const duplicatePage = useCallback((idx) => {
		const page = pages[idx];
		if (!page || (page.pageId || page._id) === REGISTRATION_PAGE_ID) return;
		const clone = {
			...page,
			pageId: genId(),
			order: pages.length,
			name: `${page.name || "Página"} (copia)`,
			elements: (page.elements || []).map((e) => ({
				...JSON.parse(JSON.stringify(e)),
				elementId: genId(),
			})),
		};
		setPages((p) => {
			const next = [...p];
			next.splice(idx + 1, 0, clone);
			return next.map((p_, i) => ({ ...p_, order: i }));
		});
		setCurrentPageIndex(idx + 1);
		setSelectedElement(null);
	}, [pages]);

	const reorderPages = useCallback((fromIdx, toIdx) => {
		if (fromIdx === toIdx) return;
		const pid = (p) => (p.pageId || p._id);
		const arr = [...pages];
		const [removed] = arr.splice(fromIdx, 1);
		arr.splice(toIdx, 0, removed);
		const regIdx = arr.findIndex((p) => pid(p) === REGISTRATION_PAGE_ID);
		if (regIdx >= 0) {
			for (let i = 0; i < regIdx; i++) {
				if (pageHasQuizOrPoints(arr[i])) {
					alertError({
						customMessage:
							"No puede haber ninguna página con quiz o que sume/reste puntos antes de la página de registro.",
					});
					return;
				}
			}
		}
		setPages((p) => {
			const next = [...p];
			const [rem] = next.splice(fromIdx, 1);
			next.splice(toIdx, 0, rem);
			return next.map((page, i) => ({ ...page, order: i }));
		});
		setCurrentPageIndex((i) => {
			if (i === fromIdx) return toIdx;
			if (fromIdx < i && toIdx >= i) return i - 1;
			if (fromIdx > i && toIdx <= i) return i + 1;
			return i;
		});
	}, [pages]);

	const applyTemplate = useCallback((plantilla) => {
		if (isRegistrationPage) return;
		const design = plantilla?.design || {};
		const templateElements = (design.elements || []).map((e) => ({
			...JSON.parse(JSON.stringify(e)),
			elementId: genId(),
		}));
		setPages((p) => {
			const next = [...p];
			next[currentPageIndex] = {
				...next[currentPageIndex],
				backgroundColor: design.backgroundColor || "#fff",
				elements: templateElements,
			};
			return next;
		});
		setSelectedElement(null);
	}, [currentPageIndex, isRegistrationPage]);

	const addElement = useCallback((type, overrides = {}) => {
		const base = defaultElement(type);
		const { content: contentOverrides, style: styleOverrides, ...rest } = overrides;
		const el = {
			...base,
			...rest,
			content: contentOverrides ? { ...(base.content || {}), ...contentOverrides } : base.content,
			style: styleOverrides ? { ...(base.style || {}), ...styleOverrides } : base.style,
		};
		setPages((p) => {
			const next = [...p];
			const page = { ...next[currentPageIndex], elements: [...(next[currentPageIndex].elements || []), el] };
			next[currentPageIndex] = page;
			return next;
		});
		setSelectedElement(el);
	}, [currentPageIndex]);

	const updateElement = useCallback((updated) => {
		setPages((p) => {
			const next = [...p];
			const page = next[currentPageIndex];
			page.elements = (page.elements || []).map((e) => {
				if (e.elementId !== updated.elementId) return e;
				if (activeViewport === "desktop") {
					const { breakpoints, ...rest } = updated;
					const newBreakpoints = breakpoints ? { ...breakpoints } : {};
					delete newBreakpoints.tablet;
					delete newBreakpoints.mobile;
					return {
						...rest,
						...(Object.keys(newBreakpoints).length > 0 && { breakpoints: newBreakpoints }),
					};
				}
				return {
					...e,
					...(updated.content !== undefined && { content: updated.content }),
					...(updated.style !== undefined && { style: updated.style }),
					breakpoints: {
						...(e.breakpoints || {}),
						[activeViewport]: {
							x: updated.x,
							y: updated.y,
							width: updated.width,
							height: updated.height,
						},
					},
				};
			});
			return next;
		});
		setSelectedElement(updated);
	}, [currentPageIndex, activeViewport]);

	const removeElement = useCallback((elementId) => {
		const el = elements.find((e) => e.elementId === elementId);
		if (el?.registrationLocked) return;
		setPages((p) => {
			const next = [...p];
			next[currentPageIndex].elements = (next[currentPageIndex].elements || []).filter((e) => e.elementId !== elementId);
			return next;
		});
		setSelectedElement(null);
	}, [currentPageIndex, elements]);

	// Modal editing (when editingModalButton is set) - must be before handleCanvasDrop
	const findModalButtonPage = useCallback(() => {
		if (!editingModalButton) return -1;
		return pages.findIndex((p) => (p.elements || []).some((e) => e.elementId === editingModalButton.elementId));
	}, [editingModalButton, pages]);

	const getModalDesign = useCallback(() => {
		if (!editingModalButton) return { backgroundColor: "#ffffff", elements: [], borderRadius: 12 };
		const design = editingModalButton.content?.modalDesign;
		return {
			backgroundColor: design?.backgroundColor || "#ffffff",
			elements: design?.elements || [],
			borderRadius: design?.borderRadius ?? 12,
		};
	}, [editingModalButton]);

	const updateModalDesign = useCallback(
		(updates) => {
			const pageIdx = findModalButtonPage();
			if (pageIdx < 0) return;
			setPages((p) => {
				const next = [...p];
				const page = next[pageIdx];
				const btn = (page.elements || []).find((e) => e.elementId === editingModalButton.elementId);
				if (!btn) return next;
				const design = { ...(btn.content?.modalDesign || { backgroundColor: "#ffffff", elements: [], borderRadius: 12 }), ...updates };
				const newBtn = { ...btn, content: { ...btn.content, modalDesign: design } };
				next[pageIdx] = { ...page, elements: page.elements.map((e) => (e.elementId === btn.elementId ? newBtn : e)) };
				return next;
			});
			setEditingModalButton((prev) => {
				if (!prev || prev.elementId !== editingModalButton.elementId) return prev;
				const design = { ...(prev.content?.modalDesign || { backgroundColor: "#ffffff", elements: [], borderRadius: 12 }), ...updates };
				return { ...prev, content: { ...prev.content, modalDesign: design } };
			});
		},
		[editingModalButton, findModalButtonPage]
	);

	const updateModalElement = useCallback(
		(el) => {
			updateModalDesign({
				elements: (getModalDesign().elements || []).map((e) => (e.elementId === el.elementId ? el : e)),
			});
		},
		[updateModalDesign, getModalDesign]
	);

	const addModalElement = useCallback(
		(type, overrides = {}) => {
			const base = defaultElement(type);
			const { content: co, style: so, ...rest } = overrides;
			const el = {
				...base,
				...rest,
				content: co ? { ...(base.content || {}), ...co } : base.content,
				style: so ? { ...(base.style || {}), ...so } : base.style,
			};
			updateModalDesign({ elements: [...(getModalDesign().elements || []), el] });
			setSelectedElement(el);
		},
		[updateModalDesign, getModalDesign]
	);

	const removeModalElement = useCallback(
		(elementId) => {
			updateModalDesign({
				elements: (getModalDesign().elements || []).filter((e) => e.elementId !== elementId),
			});
			setSelectedElement(null);
		},
		[updateModalDesign, getModalDesign]
	);

	const isModalEditMode = !!editingModalButton;
	const modalDim = isModalEditMode
		? (MODAL_SIZES.find((s) => s.id === (editingModalButton?.content?.modalSize || "medium")) || MODAL_SIZES[1])
		: null;
	const effectiveElements = isModalEditMode ? (getModalDesign().elements || []) : elements;
	const effectiveViewportDim = isModalEditMode ? modalDim : viewportDim;
	const effectiveUpdateElement = isModalEditMode ? updateModalElement : updateElement;
	const effectiveAddElement = isModalEditMode ? addModalElement : addElement;
	const effectiveRemoveElement = isModalEditMode ? removeModalElement : removeElement;
	const effectiveBgColor = isModalEditMode ? (getModalDesign().backgroundColor || "#ffffff") : null;
	const effectiveModalBorderRadius = isModalEditMode ? (getModalDesign().borderRadius ?? 12) : 0;

	const handleCanvasDragOver = useCallback((e) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "copy";
	}, []);

	const handleCanvasDrop = useCallback(
		(e) => {
			e.preventDefault();
			const raw = e.dataTransfer.getData("application/ersaas-media");
			if (!raw) return;
			if (!editingModalButton && isRegistrationPage) return;
			let data;
			try {
				data = JSON.parse(raw);
			} catch {
				return;
			}
			const { type, path } = data;
			if (type !== "image" && type !== "video") return;
			const box = canvasDropRef.current;
			const dim = editingModalButton ? modalDim : viewportDim;
			if (!box) return effectiveAddElement(type, { content: { url: path } });
			const rect = box.getBoundingClientRect();
			const localX = ((e.clientX - rect.left) * dim.width) / rect.width;
			const localY = ((e.clientY - rect.top) * dim.height) / rect.height;
			const def = defaultElement(type);
			const w = def.width || 200;
			const h = def.height || 80;
			const x = Math.max(0, localX - w / 2);
			const y = Math.max(0, localY - h / 2);
			effectiveAddElement(type, { content: { url: path }, x, y });
		},
		[effectiveAddElement, isRegistrationPage, viewportDim, editingModalButton, modalDim]
	);

	const handleSelectMediaForElement = useCallback(
		(path) => {
			if (!mediaPickerMode) return;
			effectiveAddElement(mediaPickerMode, { content: { url: path } });
			setMediaPickerOpen(false);
			setMediaPickerMode(null);
		},
		[mediaPickerMode, effectiveAddElement]
	);

	const handleUploadMediaForElement = useCallback(
		(e) => {
			const file = e?.target?.files?.[0];
			if (!file || !mediaPickerMode) return;
			const isImg = /\.(jpe?g|png|gif|webp|svg)$/i.test(file.name);
			const isVid = /\.(mp4|webm|ogg|mov)$/i.test(file.name);
			if (mediaPickerMode === "image" && !isImg) {
				alertError({ customMessage: "Solo se permiten imágenes." });
				return;
			}
			if (mediaPickerMode === "video" && !isVid) {
				alertError({ customMessage: "Solo se permiten videos (mp4, webm, ogg, mov)." });
				return;
			}
			uploadUserMedia(file)
				.then((res) => {
					if (res?.data?.fileURL) {
						setUserMedia((prev) => [{ path: res.data.fileURL, name: file.name, isVideo: isVid }, ...prev]);
						handleSelectMediaForElement(res.data.fileURL);
					}
				})
				.catch((err) => alertError({ error: err, customMessage: "Error al subir." }));
			e.target.value = "";
		},
		[mediaPickerMode, handleSelectMediaForElement]
	);

	const handleUpdateQuiz = useCallback(async (quizId, payload) => {
		const res = await updateQuiz(quizId, payload);
		setQuizzes((prev) => prev.map((q) => (q._id === res.data._id ? res.data : q)));
		return res;
	}, [updateQuiz]);

	const updateRegistrationDesign = useCallback((updates) => {
		if (!isRegistrationPage) return;
		setPages((p) => {
			const next = [...p];
			const page = next[currentPageIndex];
			page.registrationDesign = { ...(page.registrationDesign || {}), ...updates };
			return next;
		});
	}, [currentPageIndex, isRegistrationPage]);

	const updatePageDesign = useCallback((updates) => {
		if (isRegistrationPage) return;
		setPages((p) => {
			const next = [...p];
			const page = next[currentPageIndex];
			if (page) {
				if (updates.backgroundColor !== undefined) page.backgroundColor = updates.backgroundColor;
				if (updates.backgroundImageUrl !== undefined) page.backgroundImageUrl = updates.backgroundImageUrl;
				if (updates.backgroundImageOpacity !== undefined) page.backgroundImageOpacity = updates.backgroundImageOpacity;
				if (updates.transitionToNext !== undefined) page.transitionToNext = updates.transitionToNext;
				if (updates.transitionDuration !== undefined) page.transitionDuration = updates.transitionDuration;
			}
			return next;
		});
	}, [currentPageIndex, isRegistrationPage]);

	const applyBackgroundToAllPages = useCallback(() => {
		if (isRegistrationPage) return;
		const bg = currentPage?.backgroundColor || "#ffffff";
		const bgImg = currentPage?.backgroundImageUrl || "";
		const bgOp = currentPage?.backgroundImageOpacity;
		setPages((p) =>
			p.map((page) => {
				if ((page.pageId || page._id) === REGISTRATION_PAGE_ID) return page;
				return { ...page, backgroundColor: bg, backgroundImageUrl: bgImg, backgroundImageOpacity: bgOp };
			})
		);
	}, [currentPage?.backgroundColor, currentPage?.backgroundImageUrl, currentPage?.backgroundImageOpacity, isRegistrationPage]);

	const applyAnimationToAllPages = useCallback(() => {
		if (isRegistrationPage) return;
		const trans = currentPage?.transitionToNext || "none";
		const dur = currentPage?.transitionDuration ?? 350;
		setPages((p) =>
			p.map((page) => {
				if ((page.pageId || page._id) === REGISTRATION_PAGE_ID) return page;
				return { ...page, transitionToNext: trans, transitionDuration: dur };
			})
		);
	}, [currentPage?.transitionToNext, currentPage?.transitionDuration, isRegistrationPage]);

	const handleSelectBackgroundImage = useCallback(
		(path) => {
			updatePageDesign({ backgroundImageUrl: path || "" });
			setBackgroundPickerOpen(false);
		},
		[updatePageDesign]
	);

	const handleSelectLogo = useCallback(
		(path) => {
			updateRegistrationDesign({ logoUrl: path || "" });
			setLogoPickerOpen(false);
		},
		[updateRegistrationDesign]
	);

	const handleUploadAndSelectBackground = useCallback(
		(e) => {
			const file = e?.target?.files?.[0];
			if (!file) return;
			if (!/\.(jpe?g|png|gif|webp|svg)$/i.test(file.name)) {
				alertError({ customMessage: "Solo se permiten imágenes para el fondo." });
				return;
			}
			uploadUserMedia(file)
				.then((res) => {
					if (res?.data?.fileURL) {
						updatePageDesign({ backgroundImageUrl: res.data.fileURL });
						setUserMedia((prev) => [{ path: res.data.fileURL, name: file.name, isVideo: false }, ...prev]);
						setBackgroundPickerOpen(false);
					}
				})
				.catch((err) => alertError({ error: err, customMessage: "Error al subir la imagen." }));
			e.target.value = "";
		},
		[updatePageDesign]
	);

	const handleUploadAndSelectLogo = useCallback(
		(e) => {
			const file = e?.target?.files?.[0];
			if (!file) return;
			if (!/\.(jpe?g|png|gif|webp|svg)$/i.test(file.name)) {
				alertError({ customMessage: "Solo se permiten imágenes para el logo." });
				return;
			}
			uploadUserMedia(file)
				.then((res) => {
					if (res?.data?.fileURL) {
						updateRegistrationDesign({ logoUrl: res.data.fileURL });
						setUserMedia((prev) => [{ path: res.data.fileURL, name: file.name, isVideo: false }, ...prev]);
						setLogoPickerOpen(false);
					}
				})
				.catch((err) => alertError({ error: err, customMessage: "Error al subir la imagen." }));
			e.target.value = "";
		},
		[updateRegistrationDesign]
	);

	const [dragging, setDragging] = useState(null);
	const [resizing, setResizing] = useState(null);
	const [copiedElement, setCopiedElement] = useState(null);
	const [quizImagePickerFor, setQuizImagePickerFor] = useState(null);
	const [quizOptionImageUrl, setQuizOptionImageUrl] = useState("");
	useEffect(() => {
		if (quizImagePickerFor) {
			const pickerEl = (currentPage?.elements || []).find((e) => e.elementId === quizImagePickerFor.elementId);
			const quiz = quizzes.find((q) => q._id === pickerEl?.content?.quizId);
			const opt = quiz?.questions?.[0]?.options?.[quizImagePickerFor.optionIndex];
			setQuizOptionImageUrl(opt?.imageUrl || "");
		} else {
			setQuizOptionImageUrl("");
		}
	}, [quizImagePickerFor, currentPage?.elements, quizzes]);

	const copyElement = useCallback(() => {
		if (selectedElement) setCopiedElement(JSON.parse(JSON.stringify(selectedElement)));
	}, [selectedElement]);

	const toDesktopCoords = useCallback((x, y) => {
		if (activeViewport === "desktop") return { x: x || 0, y: y || 0 };
		const vp = VIEWPORTS.find((v) => v.id === activeViewport);
		if (!vp) return { x: x || 0, y: y || 0 };
		const scaleX = vp.width / DESKTOP.width;
		const scaleY = vp.height / DESKTOP.height;
		return { x: Math.round((x || 0) / scaleX), y: Math.round((y || 0) / scaleY) };
	}, [activeViewport]);

	const pasteElement = useCallback(() => {
		if (!copiedElement) return;
		const toPaste = copiedElement;
		const clone = JSON.parse(JSON.stringify(toPaste));
		clone.elementId = genId();
		delete clone.breakpoints;
		const { x, y } = toDesktopCoords(clone.x, clone.y);
		clone.x = x + 20;
		clone.y = y + 20;
		setPages((p) => {
			const next = [...p];
			const page = { ...next[currentPageIndex], elements: [...(next[currentPageIndex].elements || []), clone] };
			next[currentPageIndex] = page;
			return next;
		});
		const displayed = getElementsForViewport({ elements: [...(currentPage?.elements || []), clone] }, activeViewport).find((e) => e.elementId === clone.elementId);
		setSelectedElement(displayed || clone);
	}, [copiedElement, currentPageIndex, currentPage?.elements, activeViewport, toDesktopCoords]);

	const duplicateElement = useCallback(() => {
		if (!selectedElement) return;
		const baseEl = (currentPage?.elements || []).find((e) => e.elementId === selectedElement.elementId) || selectedElement;
		const clone = JSON.parse(JSON.stringify(baseEl));
		clone.elementId = genId();
		delete clone.breakpoints;
		const { x, y } = toDesktopCoords(selectedElement.x, selectedElement.y);
		clone.x = x + 20;
		clone.y = y + 20;
		setPages((p) => {
			const next = [...p];
			const page = { ...next[currentPageIndex], elements: [...(next[currentPageIndex].elements || []), clone] };
			next[currentPageIndex] = page;
			return next;
		});
		const displayed = getElementsForViewport({ elements: [...(currentPage?.elements || []), clone] }, activeViewport).find((e) => e.elementId === clone.elementId);
		setSelectedElement(displayed || clone);
	}, [selectedElement, currentPageIndex, currentPage?.elements, activeViewport, toDesktopCoords]);

	const reorderElement = useCallback(
		(dir) => {
			if (!selectedElement) return;
			const els = [...elements];
			const idx = els.findIndex((e) => e.elementId === selectedElement.elementId);
			if (idx < 0) return;
			const [el] = els.splice(idx, 1);
			let newIdx;
			if (dir === "front") newIdx = els.length;
			else if (dir === "back") newIdx = 0;
			else if (dir === "forward") newIdx = Math.min(els.length, idx + 1);
			else newIdx = Math.max(0, idx - 1);
			els.splice(newIdx, 0, el);
			setPages((p) => {
				const next = [...p];
				next[currentPageIndex] = { ...next[currentPageIndex], elements: els };
				return next;
			});
		},
		[selectedElement, elements, currentPageIndex]
	);

	useEffect(() => {
		const onKeyDown = (e) => {
			const inInput = e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable;
			if (inInput) return;
			if (e.key === "Delete" || e.key === "Backspace") {
				if (selectedElement && !selectedElement.registrationLocked) {
					e.preventDefault();
					effectiveRemoveElement(selectedElement.elementId);
				}
				return;
			}
			if (e.ctrlKey || e.metaKey) {
				if (e.key === "c" && selectedElement) {
					e.preventDefault();
					copyElement();
				} else if (e.key === "v" && copiedElement) {
					e.preventDefault();
					pasteElement();
				} else if (e.key === "d" && selectedElement) {
					e.preventDefault();
					duplicateElement();
				}
			}
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [selectedElement, copiedElement, copyElement, pasteElement, duplicateElement, effectiveRemoveElement]);

	const onDragStart = useCallback((element, e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragging({
			element,
			startClientX: e.clientX,
			startClientY: e.clientY,
			origX: element.x,
			origY: element.y,
		});
	}, []);

	const DRAG_THRESHOLD = 4;
	const SNAP_THRESHOLD = 30;
	const canvasCenterX = (effectiveViewportDim?.width ?? 960) / 2;
	const canvasCenterY = (effectiveViewportDim?.height ?? 540) / 2;

	useEffect(() => {
		if (!dragging) return;
		document.body.style.cursor = "grabbing";
		document.body.style.userSelect = "none";
		const data = { ...dragging };
		const onMove = (e) => {
			if (!(e.buttons & 1)) return;
			const dx = (e.clientX - data.startClientX) / canvasZoom;
			const dy = (e.clientY - data.startClientY) / canvasZoom;
			const movedPx = Math.sqrt((e.clientX - data.startClientX) ** 2 + (e.clientY - data.startClientY) ** 2);
			if (movedPx < DRAG_THRESHOLD) return;
			let nx = data.origX + dx;
			let ny = data.origY + dy;
			const w = data.element.width || 100;
			const h = data.element.height || 50;
			const elemCenterX = nx + w / 2;
			const elemCenterY = ny + h / 2;
			if (Math.abs(elemCenterX - canvasCenterX) < SNAP_THRESHOLD) nx = canvasCenterX - w / 2;
			if (Math.abs(elemCenterY - canvasCenterY) < SNAP_THRESHOLD) ny = canvasCenterY - h / 2;
			effectiveUpdateElement({ ...data.element, x: Math.round(nx), y: Math.round(ny) });
		};
		const onUp = (ev) => {
			if (ev && ev.type === "mouseup" && ev.button !== 0) return;
			setDragging(null);
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
		};
		const onBlur = () => {
			setDragging(null);
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
		};
		document.addEventListener("mousemove", onMove, true);
		document.addEventListener("mouseup", onUp, true);
		window.addEventListener("blur", onBlur);
		return () => {
			document.removeEventListener("mousemove", onMove, true);
			document.removeEventListener("mouseup", onUp, true);
			window.removeEventListener("blur", onBlur);
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
		};
	}, [dragging, effectiveUpdateElement, canvasZoom, canvasCenterX, canvasCenterY]);

	const onResizeStart = useCallback((element, handle, e) => {
		e.preventDefault();
		e.stopPropagation();
		setResizing({
			element,
			handle,
			startClientX: e.clientX,
			startClientY: e.clientY,
			origX: element.x,
			origY: element.y,
			origW: element.width,
			origH: element.height,
		});
	}, []);

	useEffect(() => {
		if (!resizing) return;
		document.body.style.cursor = "grabbing";
		document.body.style.userSelect = "none";
		const { element, handle, startClientX, startClientY, origX, origY, origW, origH } = resizing;
		const onMove = (e) => {
			if (!(e.buttons & 1)) return;
			const dx = (e.clientX - startClientX) / canvasZoom;
			const dy = (e.clientY - startClientY) / canvasZoom;
			let x = origX;
			let y = origY;
			let w = origW;
			let h = origH;
			if (handle === "se") {
				w = Math.max(MIN_SIZE, origW + dx);
				h = Math.max(MIN_SIZE, origH + dy);
			} else if (handle === "sw") {
				x = origX + dx;
				w = Math.max(MIN_SIZE, origW - dx);
				h = Math.max(MIN_SIZE, origH + dy);
			} else if (handle === "ne") {
				y = origY + dy;
				w = Math.max(MIN_SIZE, origW + dx);
				h = Math.max(MIN_SIZE, origH - dy);
			} else if (handle === "nw") {
				x = origX + dx;
				y = origY + dy;
				w = Math.max(MIN_SIZE, origW - dx);
				h = Math.max(MIN_SIZE, origH - dy);
			}
			effectiveUpdateElement({ ...element, x: Math.round(x), y: Math.round(y), width: Math.round(w), height: Math.round(h) });
		};
		const onUp = (ev) => {
			if (ev && ev.type === "mouseup" && ev.button !== 0) return;
			setResizing(null);
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
		};
		const onBlur = () => {
			setResizing(null);
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
		};
		document.addEventListener("mousemove", onMove, true);
		document.addEventListener("mouseup", onUp, true);
		window.addEventListener("blur", onBlur);
		return () => {
			document.removeEventListener("mousemove", onMove, true);
			document.removeEventListener("mouseup", onUp, true);
			window.removeEventListener("blur", onBlur);
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
		};
	}, [resizing, effectiveUpdateElement, canvasZoom]);

	const ZOOM_MIN = 0.5;
	const ZOOM_MAX = 2;
	const ZOOM_STEP = 0.1;
	const PAN_STEP = 40;

	useEffect(() => {
		const onKeyDown = (e) => {
			const inInput = e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable;
			if (inInput) return;
			if (e.ctrlKey || e.metaKey) {
				if (e.key === "=" || e.key === "+") {
					e.preventDefault();
					setCanvasZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
				} else if (e.key === "-") {
					e.preventDefault();
					setCanvasZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
				}
			}
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, []);

	const handleCanvasWheel = useCallback((e) => {
		if (e.ctrlKey || e.metaKey) {
			e.preventDefault();
			const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
			setCanvasZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta)));
		} else {
			e.preventDefault();
			setCanvasPan((p) => ({
				x: p.x - e.deltaX,
				y: p.y - e.deltaY,
			}));
		}
	}, []);

	useEffect(() => {
		const el = canvasViewportRef.current;
		if (!el) return;
		el.addEventListener("wheel", handleCanvasWheel, { passive: false });
		return () => el.removeEventListener("wheel", handleCanvasWheel);
	}, [handleCanvasWheel]);

	const playPage = pages[playPageIndex] || pages[0];
	const playElements = getElementsForViewport(playPage, playViewport);
	const isPlayRegPage = (playPage?.pageId || playPage?._id) === REGISTRATION_PAGE_ID;
	const invertPlayTransition = (t) => (t === "slideLeft" ? "slideRight" : t === "slideRight" ? "slideLeft" : t === "slideUp" ? "slideDown" : t === "slideDown" ? "slideUp" : t);
	const handlePlayButtonAction = (el) => {
		const action = el?.content?.action;
		if (action === "nextPage" && playPageIndex < pages.length - 1) {
			const pg = pages[playPageIndex];
			setPlayTransitionType(pg?.transitionToNext || "none");
			setPlayTransitionDuration(pg?.transitionDuration ?? 350);
			setPlayTransitionKey((k) => k + 1);
			setPlayPageIndex((i) => i + 1);
		} else if (action === "prevPage" && playPageIndex > 0) {
			const pg = pages[playPageIndex - 1];
			setPlayTransitionType(invertPlayTransition(pg?.transitionToNext || "none"));
			setPlayTransitionDuration(pg?.transitionDuration ?? 350);
			setPlayTransitionKey((k) => k + 1);
			setPlayPageIndex((i) => i - 1);
		} else if (action === "openModal" && el?.content?.modalDesign) {
			const sizeDef = MODAL_SIZES.find((s) => s.id === (el.content?.modalSize || "medium")) || MODAL_SIZES[1];
			setPlayModalContent({
				design: el.content.modalDesign,
				width: sizeDef.width,
				height: sizeDef.height,
			});
		}
	};
	const handlePlayPrev = () => {
		if (playPageIndex <= 0) return;
		const pg = pages[playPageIndex - 1];
		setPlayTransitionType(invertPlayTransition(pg?.transitionToNext || "none"));
		setPlayTransitionDuration(pg?.transitionDuration ?? 350);
		setPlayTransitionKey((k) => k + 1);
		setPlayPageIndex((i) => i - 1);
	};
	const handlePlayNext = () => {
		if (playPageIndex >= pages.length - 1) return;
		const pg = pages[playPageIndex];
		setPlayTransitionType(pg?.transitionToNext || "none");
		setPlayTransitionDuration(pg?.transitionDuration ?? 350);
		setPlayTransitionKey((k) => k + 1);
		setPlayPageIndex((i) => i + 1);
	};
	const handleExitPlay = () => {
		if (document.exitFullscreen) document.exitFullscreen();
		else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
		setPlayMode(false);
		setPlayQuizAnswers({});
		setPlayModalContent(null);
	};
	const handleQuizOptionSelect = (elementId, optionIndex, correct) => {
		setPlayQuizAnswers((prev) => ({ ...prev, [elementId]: { selectedIndex: optionIndex, correct } }));
	};
	const handleQuizHintUsed = (elementId, penalty) => {
		if (penalty > 0) setPlayQuizAnswers((prev) => {
			const current = prev[elementId] || {};
			return { ...prev, [elementId]: { ...current, hintPenaltyUsed: (current.hintPenaltyUsed || 0) + penalty } };
		});
	};
	const bgImgUrl = playPage?.backgroundImageUrl;
	const bgImgResolved = bgImgUrl && (bgImgUrl.startsWith("http") || bgImgUrl.startsWith("/") || bgImgUrl.startsWith("data:")) ? bgImgUrl : bgImgUrl ? `${SERVER_URL}/${bgImgUrl}` : null;
	const playViewportDim = VIEWPORTS.find((v) => v.id === playViewport) || { width: CANVAS_WIDTH, height: CANVAS_HEIGHT };

	return playMode ? (
		<PlayModeView
			playModeContainerRef={playModeContainerRef}
			pages={pages}
			playPageIndex={playPageIndex}
			playViewport={playViewport}
			playViewportDim={playViewportDim}
			playScale={playScale}
			setPlayScale={setPlayScale}
			setPlayMode={setPlayMode}
			setPlayViewport={setPlayViewport}
			playTransitionKey={playTransitionKey}
			playTransitionType={playTransitionType}
			playTransitionDuration={playTransitionDuration}
			setPlayTransitionType={setPlayTransitionType}
			setPlayTransitionDuration={setPlayTransitionDuration}
			setPlayTransitionKey={setPlayTransitionKey}
			setPlayPageIndex={setPlayPageIndex}
			playPage={playPage}
			playElements={playElements}
			isPlayRegPage={isPlayRegPage}
			bgImgResolved={bgImgResolved}
			handlePlayButtonAction={handlePlayButtonAction}
			handlePlayPrev={handlePlayPrev}
			handlePlayNext={handlePlayNext}
			handleExitPlay={handleExitPlay}
			handleQuizOptionSelect={handleQuizOptionSelect}
			handleQuizHintUsed={handleQuizHintUsed}
			setPlayHintModal={setPlayHintModal}
			playHintModal={playHintModal}
			playQuizAnswers={playQuizAnswers}
			playModalContent={playModalContent}
			setPlayModalContent={setPlayModalContent}
			quizzes={quizzes}
		/>
	) : (
		<>
		<Box display="flex" flexDirection="column" height="100vh">
			{/* Barra superior */}
			<Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1} borderBottom={1} borderColor="divider">
				<TextField
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder="Nombre del escape room"
					variant="outlined"
					size="small"
					style={{ minWidth: 200 }}
				/>
				<Box display="flex" alignItems="center" style={{ gap: 4 }}>
					<Tooltip title="Copiar (Ctrl+C)">
						<span>
							<IconButton size="small" onClick={copyElement} disabled={!selectedElement || selectedElement.registrationLocked}>
								<FileCopyIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title="Pegar (Ctrl+V)">
						<span>
							<IconButton size="small" onClick={pasteElement} disabled={!copiedElement}>
								<AssignmentIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title="Duplicar (Ctrl+D)">
						<span>
							<IconButton size="small" onClick={duplicateElement} disabled={!selectedElement || selectedElement.registrationLocked}>
								<ControlPointDuplicateIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Box style={{ width: 1, height: 20, backgroundColor: "#ddd", margin: "0 4px" }} />
					<Tooltip title="Traer adelante un paso">
						<span>
							<IconButton size="small" onClick={() => reorderElement("forward")} disabled={!selectedElement || selectedElement.registrationLocked}>
								<ArrowUpwardIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title="Enviar atrás un paso">
						<span>
							<IconButton size="small" onClick={() => reorderElement("backward")} disabled={!selectedElement || selectedElement.registrationLocked}>
								<ArrowDownwardIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title="Traer al frente">
						<span>
							<IconButton size="small" onClick={() => reorderElement("front")} disabled={!selectedElement || selectedElement.registrationLocked}>
								<FlipToFrontIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title="Enviar atrás">
						<span>
							<IconButton size="small" onClick={() => reorderElement("back")} disabled={!selectedElement || selectedElement.registrationLocked}>
								<FlipToBackIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</Box>
				<Box display="flex" alignItems="center" style={{ gap: 12 }}>
					<Button
						variant="contained"
						color="primary"
						size="small"
						startIcon={<PlayArrowIcon />}
						onClick={() => {
							setPlayPageIndex(currentPageIndex);
							setPlayViewport(activeViewport);
							setPlayMode(true);
						}}
						style={{ textTransform: "none" }}
					>
						Reproducir
					</Button>
					<Box fontSize={12} color={saving ? "#64748b" : "#94a3b8"} display="flex" alignItems="center" style={{ gap: 6 }}>
						{saving ? "Guardando…" : "Guardado automático"}
					</Box>
					{onDelete && (
						<Button color="secondary" onClick={onDelete} style={{ marginLeft: 8 }}>
							Eliminar
						</Button>
					)}
				</Box>
			</Box>

			<Box display="flex" flex={1} overflow="hidden" minHeight={0}>
				{/* Panel izquierdo: páginas */}
				<Box width={160} borderRight={1} borderColor="divider" p={1.5} overflowY="auto">
					<Box mb={1} fontWeight="bold" fontSize={12}>
						Páginas
					</Box>
					{pages.map((page, idx) => {
						const isRegistration = (page.pageId || page._id) === REGISTRATION_PAGE_ID;
						return (
						<Box
							key={page.pageId}
							onClick={() => {
								setCurrentPageIndex(idx);
								setSelectedElement(null);
							}}
							draggable={pages.length > 1}
							onDragStart={(e) => {
								e.dataTransfer.setData("text/plain", String(idx));
								e.dataTransfer.effectAllowed = "move";
							}}
							onDragOver={(e) => {
								e.preventDefault();
								e.dataTransfer.dropEffect = "move";
							}}
							onDrop={(e) => {
								e.preventDefault();
								const fromIdx = parseInt(e.dataTransfer.getData("text/plain"), 10);
								if (fromIdx === idx) return;
								reorderPages(fromIdx, idx);
							}}
							style={{
								marginBottom: 10,
								cursor: pages.length <= 1 ? "pointer" : "grab",
								position: "relative",
							}}
						>
							<Box
								style={{
									position: "relative",
									border: currentPageIndex === idx ? "2px solid #1976d2" : "1px solid #ddd",
									borderRadius: 6,
									padding: 4,
									backgroundColor: currentPageIndex === idx ? "#e8f4fd" : "#fff",
									boxShadow: currentPageIndex === idx ? "0 2px 6px rgba(25,118,210,0.2)" : "0 1px 2px rgba(0,0,0,0.08)",
								}}
							>
								<PagePreviewThumbnail
									elements={page.elements}
									registrationDesign={(page.pageId || page._id) === REGISTRATION_PAGE_ID ? page.registrationDesign : undefined}
									isRegistration={(page.pageId || page._id) === REGISTRATION_PAGE_ID}
									backgroundColor={(page.pageId || page._id) !== REGISTRATION_PAGE_ID ? page.backgroundColor : undefined}
									backgroundImageUrl={(page.pageId || page._id) !== REGISTRATION_PAGE_ID && page.backgroundImageUrl
										? (page.backgroundImageUrl.startsWith("http") || page.backgroundImageUrl.startsWith("/") || page.backgroundImageUrl.startsWith("data:")
											? page.backgroundImageUrl
											: `${SERVER_URL}/${page.backgroundImageUrl}`)
										: undefined}
									backgroundImageOpacity={(page.pageId || page._id) !== REGISTRATION_PAGE_ID ? page.backgroundImageOpacity : undefined}
									quizzes={quizzes}
								/>
								{(page.pageId || page._id) !== REGISTRATION_PAGE_ID && (
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											setPageMenuAnchor(e.currentTarget);
											setPageMenuIdx(idx);
										}}
										style={{
											position: "absolute",
											top: 2,
											right: 2,
											backgroundColor: "rgba(255,255,255,0.9)",
											width: 24,
											height: 24,
											padding: 0,
										}}
									>
										<MoreVertIcon style={{ fontSize: 16 }} />
									</IconButton>
								)}
							</Box>
						</Box>
						);
					})}
					<Menu
						anchorEl={pageMenuAnchor}
						open={pageMenuAnchor !== null}
						onClose={() => { setPageMenuAnchor(null); setPageMenuIdx(null); }}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						transformOrigin={{ vertical: "top", horizontal: "right" }}
					>
						<MenuItem
							onClick={() => {
								if (pageMenuIdx !== null) duplicatePage(pageMenuIdx);
								setPageMenuAnchor(null);
								setPageMenuIdx(null);
							}}
						>
							<ListItemIcon><ControlPointDuplicateIcon fontSize="small" /></ListItemIcon>
							<ListItemText>Duplicar</ListItemText>
						</MenuItem>
						{pages.length > 1 && (
							<MenuItem
								onClick={() => {
									if (pageMenuIdx !== null) removePage(pageMenuIdx);
									setPageMenuAnchor(null);
									setPageMenuIdx(null);
								}}
								style={{ color: "#d32f2f" }}
							>
								<ListItemIcon><DeleteIcon fontSize="small" style={{ color: "#d32f2f" }} /></ListItemIcon>
								<ListItemText>Eliminar</ListItemText>
							</MenuItem>
						)}
					</Menu>
					<Box
						onClick={addPage}
						style={{
							width: THUMB_WIDTH + 8,
							height: THUMB_HEIGHT + 8,
							border: "2px dashed #bbb",
							borderRadius: 6,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							cursor: "pointer",
							backgroundColor: "#f8f9fa",
							transition: "all 0.15s ease",
						}}
						onMouseEnter={(e) => {
							e.currentTarget.style.borderColor = "#1976d2";
							e.currentTarget.style.backgroundColor = "#e3f2fd";
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.borderColor = "#bbb";
							e.currentTarget.style.backgroundColor = "#f8f9fa";
						}}
					>
						<AddIcon style={{ fontSize: 32, color: "#999" }} />
					</Box>
				</Box>

				{/* Canvas central */}
				<Box flex={1} display="flex" flexDirection="column" overflow="hidden" bgcolor="#e5e7eb">
					<Box
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						py={1}
						px={2}
						bgcolor="#f3f4f6"
						borderBottom="1px solid #e5e7eb"
						style={{ gap: 8 }}
					>
						<Box display="flex" alignItems="center" style={{ gap: 4 }}>
							{isModalEditMode ? (
								<>
									<Button
										size="small"
										variant="outlined"
										startIcon={<CloseIcon />}
										onClick={() => { setEditingModalButton(null); setSelectedElement(null); }}
										style={{ textTransform: "none" }}
									>
										Cerrar editor de modal
									</Button>
									<Box fontSize={12} color="#64748b" ml={1}>
										Modal {modalDim?.label} ({effectiveViewportDim?.width}×{effectiveViewportDim?.height})
									</Box>
								</>
							) : (
								<>
									{VIEWPORTS.map((vp) => (
										<Tooltip key={vp.id} title={vp.label} placement="bottom">
											<IconButton
												size="small"
												onClick={() => setActiveViewport(vp.id)}
												style={{
													backgroundColor: activeViewport === vp.id ? "#7c3aed" : "transparent",
													color: activeViewport === vp.id ? "#fff" : "#64748b",
												}}
											>
												<vp.Icon fontSize="small" />
											</IconButton>
										</Tooltip>
									))}
									<Box fontSize={11} color="#64748b" ml={0.5}>
										{viewportDim.width}×{viewportDim.height}
									</Box>
								</>
							)}
							{!isModalEditMode && activeViewport !== "desktop" && !isRegistrationPage && (
								<Tooltip title="Aplicar diseño de escritorio (se adaptará al tamaño actual)">
									<Button
										size="small"
										variant="outlined"
										onClick={() => {
											setPages((p) => {
												const next = [...p];
												const page = next[currentPageIndex];
												page.elements = (page.elements || []).map((e) => {
													const { [activeViewport]: _, ...rest } = e.breakpoints || {};
													return { ...e, breakpoints: Object.keys(rest).length ? rest : undefined };
												});
												return next;
											});
										}}
										style={{ marginLeft: 8, fontSize: 10 }}
									>
										Desde escritorio
									</Button>
								</Tooltip>
							)}
						</Box>
						<Box display="flex" alignItems="center" style={{ gap: 8 }}>
						<Tooltip title="Alejar (Ctrl-)">
							<IconButton size="small" onClick={() => setCanvasZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}>
								<Box fontSize={18} fontWeight="bold" style={{ lineHeight: 1 }}>−</Box>
							</IconButton>
						</Tooltip>
						<Box fontSize={12} fontWeight={600} minWidth={48} textAlign="center">
							{Math.round(canvasZoom * 100)}%
						</Box>
						<Tooltip title="Acercar (Ctrl+)">
							<IconButton size="small" onClick={() => setCanvasZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}>
								<Box fontSize={18} fontWeight="bold" style={{ lineHeight: 1 }}>+</Box>
							</IconButton>
						</Tooltip>
						<Box width={1} height={16} bgcolor="#d1d5db" mx={0.5} />
						<Tooltip title="Restablecer vista">
							<IconButton
								size="small"
								onClick={() => {
									setCanvasZoom(1);
									setCanvasPan({ x: 0, y: 0 });
								}}
							>
								<Box fontSize={11}>1:1</Box>
							</IconButton>
						</Tooltip>
						</Box>
					</Box>
					<Box
						ref={canvasViewportRef}
						flex={1}
						display="flex"
						alignItems="center"
						justifyContent="center"
						overflow="hidden"
						style={{ cursor: "grab", touchAction: "none" }}
					>
						<Box
							style={{
								transform: `translate(${canvasPan.x}px, ${canvasPan.y}px) scale(${canvasZoom})`,
								transformOrigin: "center center",
								transition: "none",
							}}
							onClick={() => setSelectedElement(null)}
						>
							<Box
								ref={canvasDropRef}
								onDragOver={handleCanvasDragOver}
								onDrop={handleCanvasDrop}
								style={{
									width: effectiveViewportDim.width,
									height: effectiveViewportDim.height,
									backgroundColor: effectiveBgColor != null
										? effectiveBgColor
										: isRegistrationPage
										? (currentPage?.registrationDesign?.backgroundColor || "#ffffff")
										: (currentPage?.backgroundColor || "#fff"),
									borderRadius: effectiveModalBorderRadius,
									boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
									position: "relative",
									overflow: "hidden",
								}}
							>
								{!isModalEditMode && !isRegistrationPage && currentPage?.backgroundImageUrl && (
									<div
										style={{
											position: "absolute",
											top: 0,
											left: 0,
											right: 0,
											bottom: 0,
											backgroundImage: `url(${currentPage.backgroundImageUrl.startsWith("http") || currentPage.backgroundImageUrl.startsWith("/") || currentPage.backgroundImageUrl.startsWith("data:") ? currentPage.backgroundImageUrl : `${SERVER_URL}/${currentPage.backgroundImageUrl}`})`,
											backgroundSize: "cover",
											backgroundPosition: "center",
											opacity: currentPage?.backgroundImageOpacity != null ? currentPage.backgroundImageOpacity / 100 : 1,
											pointerEvents: "none",
										}}
									/>
								)}
								{isRegistrationPage ? (
									<div
										style={{
											position: "absolute",
											top: 0,
											left: 0,
											right: 0,
											bottom: 0,
											width: "100%",
											height: "100%",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<div
											style={{
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
												gap: 12,
												padding: "24px 48px",
												background: "rgba(255,255,255,0.9)",
												borderRadius: 12,
												boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
												border: "1px solid rgba(0,0,0,0.06)",
											}}
										>
											<AssignmentIcon style={{ fontSize: 40, color: "#EB1700", opacity: 0.9 }} />
											<span style={{ fontSize: 28, fontWeight: 700, color: "#1a1a1a", letterSpacing: "0.15em", textTransform: "uppercase" }}>
												Registro
											</span>
											<span style={{ fontSize: 13, color: "#666", fontWeight: 500 }}>
												Página de inscripción
											</span>
										</div>
									</div>
								) : (
									<>
										{effectiveElements.map((el) => (
											<ElementRenderer
												key={el.elementId}
												element={el}
												selected={selectedElement?.elementId === el.elementId}
												onSelect={setSelectedElement}
												onUpdate={effectiveUpdateElement}
												onUpdateQuiz={handleUpdateQuiz}
												onDragStart={onDragStart}
												onResizeStart={onResizeStart}
												isLocked={false}
												canvasZoom={canvasZoom}
												quizData={el.type === "quiz" ? quizzes.find((q) => q._id === el.content?.quizId) : undefined}
												onRequestOptionImagePicker={(elementId, optionIndex) => setQuizImagePickerFor({ elementId, optionIndex })}
											/>
										))}
									</>
								)}
							</Box>
						</Box>
					</Box>
				</Box>

				{/* Panel derecho */}
				<Box
					width={400}
					minWidth={400}
					flexShrink={0}
					borderLeft={1}
					borderColor="divider"
					display="flex"
					flexDirection="row"
					overflow="hidden"
					minHeight={0}
					bgcolor="#fff"
				>
					<>
						<Box
							width={80}
							display="flex"
							flexDirection="column"
							alignItems="stretch"
							py={1.5}
							px={0.5}
							style={{
								background: "linear-gradient(180deg, #f8f9fa 0%, #f1f3f5 100%)",
								borderRight: "1px solid rgba(0,0,0,0.06)",
								boxShadow: "inset -1px 0 0 rgba(255,255,255,0.5)",
							}}
						>
							{(isRegistrationPage ? REGISTRATION_CATEGORIES : ADD_CATEGORIES).map(({ id, label, shortLabel, Icon }) => {
									const isEditar = id === "editar";
									const disabled = isEditar && !selectedElement && isRegistrationPage;
									return (
									<Tooltip key={id} title={disabled ? "Selecciona un elemento" : label} placement="left">
										<Box
											onClick={() => !disabled && setAddPanelCategory(id)}
											display="flex"
											flexDirection="column"
											alignItems="center"
											justifyContent="center"
											py={1.25}
											mx={0.5}
											mb={0.5}
											style={{
												cursor: disabled ? "not-allowed" : "pointer",
												opacity: disabled ? 0.5 : 1,
												borderRadius: 12,
												background: addPanelCategory === id ? "#fff" : "transparent",
												boxShadow: addPanelCategory === id ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
												border: addPanelCategory === id ? "1px solid rgba(0,0,0,0.06)" : "1px solid transparent",
												transition: "all 0.2s ease",
											}}
											onMouseEnter={(e) => {
												if (!disabled && addPanelCategory !== id) {
													e.currentTarget.style.background = "rgba(255,255,255,0.6)";
													e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)";
												}
											}}
											onMouseLeave={(e) => {
												if (!disabled && addPanelCategory !== id) {
													e.currentTarget.style.background = "transparent";
													e.currentTarget.style.boxShadow = "none";
												}
											}}
										>
											<Box
												display="flex"
												alignItems="center"
												justifyContent="center"
												style={{
													width: 36,
													height: 36,
													borderRadius: 10,
													background: addPanelCategory === id ? "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)" : "rgba(0,0,0,0.04)",
												}}
											>
												<Icon
													style={{
														fontSize: 20,
														color: addPanelCategory === id ? "#fff" : "#64748b",
													}}
												/>
											</Box>
											<Box
												fontSize={10}
												mt={0.75}
												fontWeight={addPanelCategory === id ? 600 : 500}
												style={{
													color: addPanelCategory === id ? "#7c3aed" : "#64748b",
													textAlign: "center",
													lineHeight: 1.2,
												}}
											>
												{shortLabel}
											</Box>
										</Box>
									</Tooltip>
									);
								})}
							</Box>
							<Box flex={1} minHeight={0} minWidth={0} display="flex" flexDirection="column" style={{ overflow: "hidden" }}>
								<div
									style={{
										flex: 1,
										minHeight: 0,
										overflowY: "auto",
										overflowX: "hidden",
										background: "#f1f5f9",
										borderLeft: "1px solid #e2e8f0",
										padding: 16,
									}}
								>
								{addPanelCategory === "editar" && selectedElement ? (
									<>
										<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
											<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
												<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
													<LocationOnIcon style={{ fontSize: 18, color: "#64748b" }} />
												</Box>
												Ubicación
											</Box>
											<Box display="flex" style={{ gap: 10 }}>
												<TextField label="X" type="number" size="small" value={selectedElement.x}
													onChange={(e) => effectiveUpdateElement({ ...selectedElement, x: Number(e.target.value) || 0 })}
													style={{ flex: 1 }} inputProps={{ style: { fontSize: 14 } }} />
												<TextField label="Y" type="number" size="small" value={selectedElement.y}
													onChange={(e) => effectiveUpdateElement({ ...selectedElement, y: Number(e.target.value) || 0 })}
													style={{ flex: 1 }} inputProps={{ style: { fontSize: 14 } }} />
											</Box>
											<Box display="flex" style={{ gap: 8, marginTop: 12 }}>
												<Button size="medium" variant="outlined" fullWidth
													onClick={() => {
														const vp = effectiveViewportDim || { width: CANVAS_WIDTH, height: CANVAS_HEIGHT };
														const w = selectedElement.width || 100;
														const centerX = Math.round(vp.width / 2 - w / 2);
														effectiveUpdateElement({ ...selectedElement, x: centerX });
													}}
													style={{ textTransform: "none", padding: "8px 12px" }}>
													Centrar horizontal
												</Button>
												<Button size="medium" variant="outlined" fullWidth
													onClick={() => {
														const vp = effectiveViewportDim || { width: CANVAS_WIDTH, height: CANVAS_HEIGHT };
														const h = selectedElement.height || 50;
														const centerY = Math.round(vp.height / 2 - h / 2);
														effectiveUpdateElement({ ...selectedElement, y: centerY });
													}}
													style={{ textTransform: "none", padding: "8px 12px" }}>
													Centrar vertical
												</Button>
											</Box>
										</Box>
										{selectedElement.type !== "text" && (
											<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
												<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
													<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
														<AspectRatioIcon style={{ fontSize: 18, color: "#64748b" }} />
													</Box>
													Tamaño
												</Box>
												<Box display="flex" style={{ gap: 10 }}>
													<TextField label="Ancho" type="number" size="small" value={selectedElement.width}
														onChange={(e) => effectiveUpdateElement({ ...selectedElement, width: Number(e.target.value) || 100 })}
														style={{ flex: 1 }} inputProps={{ style: { fontSize: 14 } }} />
													<TextField label="Alto" type="number" size="small" value={selectedElement.height}
														onChange={(e) => effectiveUpdateElement({ ...selectedElement, height: Number(e.target.value) || 50 })}
														style={{ flex: 1 }} inputProps={{ style: { fontSize: 14 } }} />
												</Box>
											</Box>
										)}

										{selectedElement.type === "text" && (
											<>
												<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
													<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
														<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
															<TextFieldsIcon style={{ fontSize: 18, color: "#64748b" }} />
														</Box>
														Fuente y tamaño
													</Box>
													<Box display="flex" style={{ gap: 10 }}>
														<FormControl size="small" style={{ flex: 1 }}>
															<InputLabel>Fuente</InputLabel>
															<Select value={selectedElement.style?.fontFamily || "Arial, sans-serif"}
																onChange={(e) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, fontFamily: e.target.value } })}
																label="Fuente">
																{FONT_OPTIONS.map((f) => <MenuItem key={f} value={f}>{f.split(",")[0]}</MenuItem>)}
															</Select>
														</FormControl>
														<TextField label="Tamaño" type="number" size="small" value={selectedElement.style?.fontSize || 18}
															onChange={(e) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, fontSize: Number(e.target.value) || 18 } })}
															style={{ flex: 1, minWidth: 70 }} inputProps={{ min: 8, max: 120 }} />
													</Box>
												</Box>
												<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
													<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
														<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
															<PaletteIcon style={{ fontSize: 18, color: "#64748b" }} />
														</Box>
														Colores
													</Box>
													<Box display="flex" flexDirection="column" style={{ gap: 14 }}>
														<Box display="flex" alignItems="center" style={{ gap: 10 }}>
															<HexColorPickerPopover
																label="Texto"
																value={selectedElement.style?.color || "#000000"}
																onChange={(c) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, color: c } })}
															/>
														</Box>
														<Box display="flex" alignItems="center" style={{ gap: 10, flexWrap: "wrap" }}>
															<HexColorPickerPopover
																label="Fondo"
																value={selectedElement.style?.backgroundColor === "transparent" || !selectedElement.style?.backgroundColor ? "#ffffff" : selectedElement.style.backgroundColor}
																onChange={(c) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, backgroundColor: c } })}
															/>
															<Button size="small" variant="outlined" onClick={() => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, backgroundColor: "transparent" } })} style={{ textTransform: "none" }}>
																Sin fondo
															</Button>
														</Box>
													</Box>
												</Box>
												<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
													<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5}>
														Estilo de texto
													</Box>
													<Box mb={1.5}>
														<ToggleButtonGroup size="small" exclusive={false}
															value={[(selectedElement.style?.fontWeight === "bold" && "bold") || null, (selectedElement.style?.fontStyle === "italic" && "italic") || null, (selectedElement.style?.textDecoration === "underline" && "underline") || null].filter(Boolean)}
															onChange={(_, val) => {
																if (!val) return;
																const style = { ...selectedElement.style };
																style.fontWeight = val.includes("bold") ? "bold" : "normal";
																style.fontStyle = val.includes("italic") ? "italic" : "normal";
																style.textDecoration = val.includes("underline") ? "underline" : "none";
																effectiveUpdateElement({ ...selectedElement, style });
															}}>
															<ToggleButton value="bold" style={{ textTransform: "none" }}><FormatBoldIcon /></ToggleButton>
															<ToggleButton value="italic" style={{ textTransform: "none" }}><FormatItalicIcon /></ToggleButton>
															<ToggleButton value="underline" style={{ textTransform: "none" }}><FormatUnderlinedIcon /></ToggleButton>
														</ToggleButtonGroup>
													</Box>
													<FormControl size="small" fullWidth>
														<InputLabel>Alineación</InputLabel>
														<Select value={selectedElement.style?.textAlign || "left"}
															onChange={(e) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, textAlign: e.target.value } })}
															label="Alineación">
															<MenuItem value="left">Izquierda</MenuItem>
															<MenuItem value="center">Centro</MenuItem>
															<MenuItem value="right">Derecha</MenuItem>
															<MenuItem value="justify">Justificado</MenuItem>
														</Select>
													</FormControl>
												</Box>
											</>
										)}

										{selectedElement.type === "image" && (
											<>
												<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
													<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
														<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
															<ImageIcon style={{ fontSize: 18, color: "#64748b" }} />
														</Box>
														Enlace de la imagen
													</Box>
													<TextField placeholder="Pega aquí la URL de la imagen" size="small" fullWidth variant="outlined"
														value={selectedElement.content?.url || ""}
														onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, url: e.target.value } })}
														inputProps={{ style: { fontSize: 14 } }} />
												</Box>
												<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
													<Box fontSize={13} fontWeight={600} color="#334155" mb={1}>Opacidad</Box>
													<Box fontSize={12} color="#64748b" mb={0.5}>
														{Math.round((selectedElement.style?.opacity != null ? selectedElement.style.opacity : 1) * 100)}%
													</Box>
													<Slider
														value={(selectedElement.style?.opacity != null ? selectedElement.style.opacity : 1) * 100}
														onChange={(_, v) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, opacity: v / 100 } })}
														min={0}
														max={100}
														valueLabelDisplay="auto"
														valueLabelFormat={(v) => `${v}%`}
														style={{ color: "#7c3aed" }}
													/>
												</Box>
												<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
													<Box fontSize={13} fontWeight={600} color="#334155" mb={1}>Redondeo</Box>
													<Box fontSize={12} color="#64748b" mb={0.5}>
														{selectedElement.style?.borderRadiusPercent ?? 0}%
													</Box>
													<Slider
														value={selectedElement.style?.borderRadiusPercent ?? 0}
														onChange={(_, v) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, borderRadiusPercent: v } })}
														min={0}
														max={100}
														valueLabelDisplay="auto"
														valueLabelFormat={(v) => `${v}%`}
														style={{ color: "#7c3aed" }}
													/>
												</Box>
											</>
										)}

										{selectedElement.type === "video" && (
											<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
												<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
													<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
														<VideoLibraryIcon style={{ fontSize: 18, color: "#64748b" }} />
													</Box>
													Enlace del video
												</Box>
												<TextField placeholder="Pega aquí la URL del video" size="small" fullWidth variant="outlined"
													value={selectedElement.content?.url || ""}
													onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, url: e.target.value } })}
													inputProps={{ style: { fontSize: 14 } }}
													style={{ marginBottom: 12 }} />
												<FormControlLabel
													control={
														<Switch
															checked={!!selectedElement.content?.autoplay}
															onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, autoplay: e.target.checked } })}
															color="primary"
															size="small"
														/>
													}
													label="Reproducir automáticamente (autoplay)"
													style={{ margin: 0 }}
												/>
											</Box>
										)}

										{selectedElement.type === "quiz" && (
											<>
											<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
												<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
													<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
														<TextFieldsIcon style={{ fontSize: 18, color: "#64748b" }} />
													</Box>
													Fuente de la pregunta
												</Box>
												<Box display="flex" style={{ gap: 10 }}>
													<FormControl size="small" style={{ flex: 1 }}>
														<InputLabel>Fuente</InputLabel>
														<Select value={selectedElement.style?.fontFamily || "Arial, sans-serif"}
															onChange={(e) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, fontFamily: e.target.value } })}
															label="Fuente">
															{FONT_OPTIONS.map((f) => <MenuItem key={f} value={f}>{f.split(",")[0]}</MenuItem>)}
														</Select>
													</FormControl>
													<TextField label="Tamaño" type="number" size="small" value={selectedElement.style?.fontSize || 14}
														onChange={(e) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, fontSize: Number(e.target.value) || 14 } })}
														style={{ flex: 1, minWidth: 70 }} inputProps={{ min: 8, max: 120 }} />
												</Box>
												<Box display="flex" flexDirection="column" style={{ gap: 10, marginTop: 12 }}>
													<HexColorPickerPopover label="Color texto" value={selectedElement.style?.color || "#1e293b"}
														onChange={(c) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, color: c } })} />
													<ToggleButtonGroup size="small" exclusive={false}
														value={[(selectedElement.style?.fontWeight === "bold" && "bold") || null, (selectedElement.style?.fontStyle === "italic" && "italic") || null].filter(Boolean)}
														onChange={(_, val) => {
															if (!val) return;
															const st = { ...selectedElement.style };
															st.fontWeight = val.includes("bold") ? "bold" : "normal";
															st.fontStyle = val.includes("italic") ? "italic" : "normal";
															effectiveUpdateElement({ ...selectedElement, style: st });
														}}>
														<ToggleButton value="bold" style={{ textTransform: "none" }}><FormatBoldIcon /></ToggleButton>
														<ToggleButton value="italic" style={{ textTransform: "none" }}><FormatItalicIcon /></ToggleButton>
													</ToggleButtonGroup>
													<FormControl size="small" fullWidth>
														<InputLabel>Alineación</InputLabel>
														<Select value={selectedElement.style?.textAlign || "left"}
															onChange={(e) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, textAlign: e.target.value } })}
															label="Alineación">
															<MenuItem value="left">Izquierda</MenuItem>
															<MenuItem value="center">Centro</MenuItem>
															<MenuItem value="right">Derecha</MenuItem>
															<MenuItem value="justify">Justificado</MenuItem>
														</Select>
													</FormControl>
												</Box>
											</Box>
											<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
												<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
													<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
														<FormatListBulletedIcon style={{ fontSize: 18, color: "#64748b" }} />
													</Box>
													Opciones del quiz
												</Box>
												<Box display="flex" flexDirection="column" style={{ gap: 12 }}>
													<Box display="flex" style={{ gap: 10, flexWrap: "wrap" }}>
														<FormControl size="small" style={{ minWidth: 100 }}>
															<InputLabel>Fuente</InputLabel>
															<Select value={selectedElement.content?.quizOptionsStyle?.fontFamily || "Arial, sans-serif"}
																onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, quizOptionsStyle: { ...selectedElement.content?.quizOptionsStyle, fontFamily: e.target.value } } })}
																label="Fuente">
																{FONT_OPTIONS.map((f) => <MenuItem key={f} value={f}>{f.split(",")[0]}</MenuItem>)}
															</Select>
														</FormControl>
														<TextField label="Tamaño" type="number" size="small" value={selectedElement.content?.quizOptionsStyle?.fontSize ?? 12}
															onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, quizOptionsStyle: { ...selectedElement.content?.quizOptionsStyle, fontSize: Number(e.target.value) || 12 } } })}
															style={{ width: 72 }} inputProps={{ min: 8, max: 120 }} />
													</Box>
													<TextField
														label="Alt. imágenes"
														type="number"
														size="small"
														fullWidth
														value={selectedElement.content?.quizOptionsImageMaxHeight ?? 120}
														onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, quizOptionsImageMaxHeight: Math.max(40, Math.min(400, Number(e.target.value) || 120)) } })}
														inputProps={{ min: 40, max: 400 }}
													/>
													<Box display="flex" alignItems="center" style={{ gap: 10 }}>
														<HexColorPickerPopover label="Color texto" value={selectedElement.content?.quizOptionsStyle?.color || "#334155"}
															onChange={(c) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, quizOptionsStyle: { ...selectedElement.content?.quizOptionsStyle, color: c } } })} />
														<ToggleButtonGroup size="small" exclusive={false}
															value={[(selectedElement.content?.quizOptionsStyle?.fontWeight === "bold" && "bold") || null, (selectedElement.content?.quizOptionsStyle?.fontStyle === "italic" && "italic") || null].filter(Boolean)}
															onChange={(_, val) => {
																if (!val) return;
																const st = { ...selectedElement.content?.quizOptionsStyle };
																st.fontWeight = val.includes("bold") ? "bold" : "normal";
																st.fontStyle = val.includes("italic") ? "italic" : "normal";
																effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, quizOptionsStyle: st } });
															}}>
															<ToggleButton value="bold" style={{ textTransform: "none" }}><FormatBoldIcon /></ToggleButton>
															<ToggleButton value="italic" style={{ textTransform: "none" }}><FormatItalicIcon /></ToggleButton>
														</ToggleButtonGroup>
													</Box>
													<FormControl size="small" fullWidth>
														<InputLabel>Alineación</InputLabel>
														<Select value={selectedElement.content?.quizOptionsStyle?.textAlign || "left"}
															onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, quizOptionsStyle: { ...selectedElement.content?.quizOptionsStyle, textAlign: e.target.value } } })}
															label="Alineación">
															<MenuItem value="left">Izquierda</MenuItem>
															<MenuItem value="center">Centro</MenuItem>
															<MenuItem value="right">Derecha</MenuItem>
															<MenuItem value="justify">Justificado</MenuItem>
														</Select>
													</FormControl>
												</Box>
											</Box>
											<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
												<Box fontSize={13} fontWeight={600} color="#334155" mb={2} display="flex" alignItems="center" style={{ gap: 8 }}>
													<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
														<LiveHelpIcon style={{ fontSize: 18, color: "#64748b" }} />
													</Box>
													Pregunta (texto en el canvas)
												</Box>
												{selectedElement.content?.quizId ? (
													<InlineQuizEditor
														quiz={quizzes.find((q) => q._id === selectedElement.content?.quizId)}
														onSave={(saved) => {
															setQuizzes((prev) => prev.map((q) => (q._id === saved._id ? saved : q)));
														}}
														updateQuiz={updateQuiz}
														userMedia={userMedia}
													/>
												) : (
													<Box>
														<Button
															fullWidth
															variant="contained"
															size="small"
															onClick={async () => {
																try {
																	const res = await createQuiz({
																		fullName: "Pregunta",
																		questions: [{ type: "multipleChoice", text: "Pregunta", options: [{ text: "Opción 1", imageUrl: "", correct: true }], explanation: "" }],
																		pointsCorrect: 10,
																		pointsIncorrect: -5,
																		allowMultipleAttempts: false,
																	});
																	loadQuizzes();
																	effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, quizId: res.data._id } });
																} catch (e) {
																	alertError({ error: e, customMessage: "No se pudo crear la pregunta." });
																}
															}}
															style={{ textTransform: "none", background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)" }}
														>
															Crear pregunta
														</Button>
													</Box>
												)}
											</Box>
											</>
										)}

							{selectedElement.type === "hint" && (
								<>
									<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
										<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
											<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
												<LiveHelpIcon style={{ fontSize: 18, color: "#7c3aed" }} />
											</Box>
											Pista
										</Box>
										<TextField label="Texto del botón" placeholder="Pista" size="small" fullWidth variant="outlined"
											value={selectedElement.content?.label || "Pista"}
											onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, label: e.target.value || "Pista" } })}
											style={{ marginBottom: 12 }} />
										<TextField label="Contenido (lo que verá el jugador)" placeholder="Escribe la pista..." size="small" fullWidth variant="outlined" multiline rows={3}
											value={selectedElement.content?.hintText || ""}
											onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, hintText: e.target.value } })}
											style={{ marginBottom: 12 }} />
										<TextField label="Penalización (pts)" type="number" size="small" fullWidth variant="outlined"
											value={selectedElement.content?.hintPenalty ?? 0}
											onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, hintPenalty: Math.max(0, Number(e.target.value) || 0) } })}
											inputProps={{ min: 0 }} helperText="0 = sin penalización" style={{ marginBottom: 12 }} />
									</Box>
									<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
										<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
											<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
												<PaletteIcon style={{ fontSize: 18, color: "#64748b" }} />
											</Box>
											Apariencia
										</Box>
										<FormControl size="small" fullWidth style={{ marginBottom: 12 }}>
											<InputLabel>Fondo</InputLabel>
											<Select value={(selectedElement.style?.backgroundColor || "rgba(245, 243, 255, 1)") === "transparent" ? "transparent" : "color"}
												onChange={(e) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, backgroundColor: e.target.value === "transparent" ? "transparent" : "rgba(245, 243, 255, 1)" } })}
												label="Fondo">
												<MenuItem value="transparent">Sin fondo</MenuItem>
												<MenuItem value="color">Con color</MenuItem>
											</Select>
										</FormControl>
										{(selectedElement.style?.backgroundColor || "rgba(245, 243, 255, 1)") !== "transparent" && (
											<Box mb={1.5}><RgbaColorPickerPopover label="Color de fondo" value={selectedElement.style?.backgroundColor || "rgba(245, 243, 255, 1)"}
												onChange={(color) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, backgroundColor: color } })} /></Box>
										)}
										<Box mb={1.5}><RgbaColorPickerPopover label="Color texto e icono" value={selectedElement.style?.color || "rgba(124, 58, 237, 1)"}
											onChange={(color) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, color } })} /></Box>
										<Box mb={1.5}><RgbaColorPickerPopover label="Color del borde" value={selectedElement.style?.borderColor || "rgba(124, 58, 237, 1)"}
											onChange={(color) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, borderColor: color } })} /></Box>
										<TextField label="Grosor del borde (px)" type="number" size="small" inputProps={{ min: 0 }}
											value={selectedElement.style?.borderWidth ?? 1}
											onChange={(e) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, borderWidth: Math.max(0, Number(e.target.value) || 1) } })}
											fullWidth style={{ marginBottom: 12 }} />
										<TextField label="Redondeo (0=recto, 50=redondeado)" type="number" size="small" inputProps={{ min: 0 }}
											value={selectedElement.style?.borderRadius ?? 8}
											onChange={(e) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, borderRadius: Math.max(0, Number(e.target.value) || 0) } })}
											fullWidth style={{ marginBottom: 12 }} />
										<TextField label="Tamaño de fuente" type="number" size="small" inputProps={{ min: 10, max: 72 }}
											value={selectedElement.style?.fontSize ?? 14}
											onChange={(e) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, fontSize: Math.max(10, Math.min(72, Number(e.target.value) || 14)) } })}
											fullWidth helperText="px" />
									</Box>
								</>
							)}

							{selectedElement.type === "button" && (
								<>
									<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
										<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
											<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
												<ArrowForwardIcon style={{ fontSize: 18, color: "#64748b" }} />
											</Box>
											Contenido del botón
										</Box>
										<TextField placeholder="Texto (opcional)" size="small" fullWidth variant="outlined"
											value={selectedElement.content?.label || ""}
											onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, label: e.target.value } })}
											style={{ marginBottom: 12 }} inputProps={{ style: { fontSize: 14 } }} />
										<Box fontSize={12} color="#64748b" mb={1}>Icono — clic para elegir:</Box>
										<Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
											{["→", "▶", "✓", "✕", "💡", "📷", "🎯", "👉", "⭐", "🔒"].map((emoji) => (
												<Box key={emoji} onClick={() => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, icon: (selectedElement.content?.icon || "") === emoji ? "" : emoji } })}
													style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, borderRadius: 10,
														border: (selectedElement.content?.icon || "") === emoji ? "2px solid #7c3aed" : "1px solid #e2e8f0",
														background: (selectedElement.content?.icon || "") === emoji ? "#f5f3ff" : "#fff", cursor: "pointer" }}>
													{emoji}
												</Box>
											))}
											<Box key="arrowRight" onClick={() => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, icon: (selectedElement.content?.icon || "") === "arrowRight" ? "" : "arrowRight" } })}
												style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 10,
													border: (selectedElement.content?.icon || "") === "arrowRight" ? "2px solid #7c3aed" : "1px solid #e2e8f0",
													background: (selectedElement.content?.icon || "") === "arrowRight" ? "#f5f3ff" : "#fff", cursor: "pointer", color: "#334155" }}
												title="Flecha siguiente (como en la web)">
												<ArrowForwardIcon style={{ fontSize: 22 }} />
											</Box>
											<Box onClick={() => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, icon: "" } })}
												style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, borderRadius: 10,
													border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", color: "#64748b" }}>Quitar</Box>
										</Box>
										{(selectedElement.content?.icon || "") && (
											<Box display="flex" flexDirection="column" style={{ gap: 12, marginTop: 12 }}>
												<FormControl size="small" fullWidth>
													<InputLabel>Posición del icono</InputLabel>
													<Select value={selectedElement.content?.iconPosition || "before"}
														onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, iconPosition: e.target.value } })}
														label="Posición del icono">
														<MenuItem value="before">Antes del texto</MenuItem>
														<MenuItem value="after">Después del texto</MenuItem>
													</Select>
												</FormControl>
												<FormControl size="small" fullWidth>
													<InputLabel>Tamaño del icono</InputLabel>
													<Select value={selectedElement.content?.iconSize ?? 1.2}
														onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, iconSize: Number(e.target.value) } })}
														label="Tamaño del icono">
														<MenuItem value={0.8}>Pequeño</MenuItem>
														<MenuItem value={1}>Normal</MenuItem>
														<MenuItem value={1.2}>Mediano</MenuItem>
														<MenuItem value={1.5}>Grande</MenuItem>
													</Select>
												</FormControl>
											</Box>
										)}
									</Box>
									<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
										<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
											<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
												<PaletteIcon style={{ fontSize: 18, color: "#64748b" }} />
											</Box>
											Apariencia
										</Box>
										<FormControl size="small" fullWidth style={{ marginBottom: 12 }}>
											<InputLabel>Fondo</InputLabel>
											<Select value={(selectedElement.style?.backgroundColor || "transparent") === "transparent" ? "transparent" : "color"}
												onChange={(e) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, backgroundColor: e.target.value === "transparent" ? "transparent" : "rgba(124, 58, 237, 1)" } })}
												label="Fondo">
												<MenuItem value="transparent">Sin fondo</MenuItem>
												<MenuItem value="color">Con color</MenuItem>
											</Select>
										</FormControl>
										{(selectedElement.style?.backgroundColor || "transparent") !== "transparent" && (
											<Box mb={1.5}><RgbaColorPickerPopover label="Color de fondo" value={selectedElement.style?.backgroundColor || "rgba(124, 58, 237, 1)"}
												onChange={(color) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, backgroundColor: color } })} /></Box>
										)}
										<Box mb={1.5}><RgbaColorPickerPopover label="Color texto e icono" value={selectedElement.style?.color || "rgba(51, 51, 51, 1)"}
											onChange={(color) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, color } })} /></Box>
										<TextField label="Redondeo (0=recto, 50=redondeado)" type="number" size="small" inputProps={{ min: 0 }}
											value={selectedElement.style?.borderRadius ?? 8}
											onChange={(e) => effectiveUpdateElement({ ...selectedElement, style: { ...selectedElement.style, borderRadius: Math.max(0, Number(e.target.value) || 0) } })}
											fullWidth />
									</Box>
									<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
										<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
											<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
												<TouchAppIcon style={{ fontSize: 18, color: "#64748b" }} />
											</Box>
											Al hacer clic
										</Box>
										<FormControl size="small" fullWidth>
											<InputLabel>Qué hace el botón</InputLabel>
											<Select value={selectedElement.content?.action || (isModalEditMode ? "closeModal" : "nextPage")}
												onChange={(e) => {
													const action = e.target.value;
													const next = { ...selectedElement.content, action };
													if (action === "openModal") {
														next.modalSize = next.modalSize || "medium";
														next.modalDesign = next.modalDesign || { backgroundColor: "#ffffff", elements: [], borderRadius: 12 };
													}
													effectiveUpdateElement({ ...selectedElement, content: next });
												}}
												label="Qué hace el botón">
												{isModalEditMode ? (
													<MenuItem value="closeModal">Cerrar modal</MenuItem>
												) : (
													<>
														<MenuItem value="nextPage">Siguiente página</MenuItem>
														<MenuItem value="prevPage">Página anterior</MenuItem>
														<MenuItem value="openModal">Abrir modal</MenuItem>
													</>
												)}
											</Select>
										</FormControl>
										{selectedElement.content?.action === "openModal" && (
											<>
												<FormControl size="small" fullWidth style={{ marginTop: 16 }}>
													<InputLabel>Tamaño del modal</InputLabel>
													<Select value={selectedElement.content?.modalSize || "medium"}
														onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, modalSize: e.target.value } })}
														label="Tamaño del modal">
														{MODAL_SIZES.map((s) => <MenuItem key={s.id} value={s.id}>{s.label} ({s.width}×{s.height})</MenuItem>)}
													</Select>
												</FormControl>
												<Button fullWidth variant="contained" color="primary" startIcon={<EditIcon />}
													onClick={() => setEditingModalButton(selectedElement)}
													style={{ marginTop: 16, textTransform: "none" }}>
													Editar modal
												</Button>
											</>
										)}
									</Box>
								</>
							)}

							{selectedElement.type === "shape" && (
								<>
									<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
										<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
											<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
												<CategoryIcon style={{ fontSize: 18, color: "#64748b" }} />
											</Box>
											Tipo de forma
										</Box>
										<FormControl size="small" fullWidth variant="outlined">
											<InputLabel>Tipo</InputLabel>
											<Select value={selectedElement.content?.shapeType || "square"}
												onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, shapeType: e.target.value } })}
												label="Tipo">
												{SHAPE_TYPES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
											</Select>
										</FormControl>
									</Box>
									<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
										<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
											<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
												<PaletteIcon style={{ fontSize: 18, color: "#64748b" }} />
											</Box>
											Colores
										</Box>
										<Box mb={1.5}>
											<ShapeColorPicker label="Relleno" value={selectedElement.content?.fillColor}
												onChange={(color) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, fillColor: color } })} />
										</Box>
										<Box mb={1.5}>
											<ShapeColorPicker label="Borde" value={selectedElement.content?.strokeColor}
												onChange={(color) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, strokeColor: color } })} />
										</Box>
										<Box display="flex" style={{ gap: 10 }}>
											<TextField label="Grosor borde" type="number" size="small" value={selectedElement.content?.strokeWidth ?? 2}
												onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, strokeWidth: Math.max(0, Number(e.target.value) || 0) } })}
												style={{ flex: 1 }} inputProps={{ min: 0, max: 20 }} variant="outlined" />
											{(selectedElement.content?.shapeType === "square" || selectedElement.content?.shapeType === "line" || selectedElement.content?.shapeType === "triangle") && (
												<TextField label="Redondeo" type="number" size="small" value={selectedElement.content?.borderRadius ?? 0}
													onChange={(e) => effectiveUpdateElement({ ...selectedElement, content: { ...selectedElement.content, borderRadius: Math.max(0, Number(e.target.value) || 0) } })}
													style={{ flex: 1 }} inputProps={{ min: 0 }} variant="outlined" />
											)}
										</Box>
									</Box>
								</>
							)}

							<Box mt={2} pt={2} style={{ borderTop: "1px solid #e2e8f0" }}>
							<Button
								fullWidth
								color="secondary"
								variant="outlined"
								size="medium"
								startIcon={<DeleteIcon />}
								onClick={() => effectiveRemoveElement(selectedElement.elementId)}
								style={{ textTransform: "none", padding: "10px 16px" }}
							>
								Eliminar elemento
							</Button>
							</Box>
									</>
								) : addPanelCategory === "editar" && !selectedElement && isRegistrationPage ? (
									<Box p={2} textAlign="center" color="text.secondary" fontSize={13}>
										Selecciona un elemento en el lienzo para editarlo
									</Box>
								) : addPanelCategory === "editar" && !selectedElement && isModalEditMode ? (
									<Box mb={3} p={2.5} borderRadius={12} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
										<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>
											<Box p={0.75} borderRadius={8} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
												<PaletteIcon style={{ fontSize: 18, color: "#64748b" }} />
											</Box>
											Diseño del modal
										</Box>
										<Box mb={1.5}>
											<RgbaColorPickerPopover label="Color de fondo" value={getModalDesign().backgroundColor || "#ffffff"}
												onChange={(color) => updateModalDesign({ backgroundColor: color })} />
										</Box>
										<TextField label="Redondeo (0=recto, 24=muy redondeado)" type="number" size="small" fullWidth
											value={getModalDesign().borderRadius ?? 12}
											onChange={(e) => updateModalDesign({ borderRadius: Math.max(0, Math.min(48, Number(e.target.value) || 0)) })}
											inputProps={{ min: 0, max: 48 }} />
									</Box>
								) : addPanelCategory === "editar" && !selectedElement && !isRegistrationPage ? (
									<>
										<Box mb={3} p={2.5} borderRadius={14} style={{ background: "linear-gradient(135deg, #fafbfc 0%, #f1f5f9 100%)", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
											<Box fontSize={13} fontWeight={600} color="#334155" mb={2} display="flex" alignItems="center" style={{ gap: 10 }}>
												<Box p={0.9} borderRadius={10} style={{ background: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
													<PaletteIcon style={{ fontSize: 20, color: "#7c3aed" }} />
												</Box>
												Fondo de la página
											</Box>
											<Box mb={2}>
												<Box fontSize={12} fontWeight={600} color="#475569" mb={1}>Imagen</Box>
												<Box
													onClick={() => setBackgroundPickerOpen(true)}
													display="flex"
													alignItems="center"
													style={{
														gap: 12, padding: 12, borderRadius: 12, background: "#fff",
														border: "1px solid #e2e8f0", cursor: "pointer", transition: "all 0.2s ease",
													}}
													onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#7c3aed"; e.currentTarget.style.boxShadow = "0 0 0 1px rgba(124,58,237,0.2)"; }}
													onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.boxShadow = "none"; }}
												>
													<Box style={{ width: 56, height: 42, borderRadius: 8, flexShrink: 0, background: currentPage?.backgroundImageUrl ? `url(${currentPage.backgroundImageUrl.startsWith("http") || currentPage.backgroundImageUrl.startsWith("/") || currentPage.backgroundImageUrl.startsWith("data:") ? currentPage.backgroundImageUrl : `${SERVER_URL}/${currentPage.backgroundImageUrl}`}) center/cover` : "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)", border: currentPage?.backgroundImageUrl ? "none" : "2px dashed #cbd5e1" }} />
													<Box flex={1} minWidth={0}>
														<Box fontSize={13} fontWeight={600} color="#1e293b">{currentPage?.backgroundImageUrl ? "Imagen asignada" : "Sin imagen"}</Box>
														<Box fontSize={11} color="#94a3b8">{currentPage?.backgroundImageUrl ? "Clic para cambiar" : "Clic para elegir o subir"}</Box>
													</Box>
													{currentPage?.backgroundImageUrl ? (
														<IconButton size="small" onClick={(e) => { e.stopPropagation(); updatePageDesign({ backgroundImageUrl: "" }); }} title="Quitar imagen">
															<DeleteIcon style={{ fontSize: 18, color: "#94a3b8" }} />
														</IconButton>
													) : (
														<ImageIcon style={{ fontSize: 22, color: "#cbd5e1" }} />
													)}
												</Box>
												{currentPage?.backgroundImageUrl && (
													<Box mt={1.5}>
														<Box fontSize={12} fontWeight={600} color="#475569" mb={0.5}>
															Opacidad imagen: {currentPage?.backgroundImageOpacity != null ? Math.round(currentPage.backgroundImageOpacity) : 100}%
														</Box>
														<Slider
															value={currentPage?.backgroundImageOpacity != null ? currentPage.backgroundImageOpacity : 100}
															onChange={(_, v) => updatePageDesign({ backgroundImageOpacity: v })}
															min={0} max={100} valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}%`}
															style={{ color: "#7c3aed" }}
														/>
													</Box>
												)}
											</Box>
											<input ref={backgroundFileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleUploadAndSelectBackground} />
											<Box mb={2}>
												<Box fontSize={12} fontWeight={600} color="#475569" mb={1}>Color</Box>
												<Box display="flex" alignItems="center" style={{ gap: 10 }}>
													<HexColorPickerPopover value={currentPage?.backgroundColor || "#ffffff"} onChange={(c) => updatePageDesign({ backgroundColor: c })} />
													<Box fontSize={12} color="#64748b" style={{ fontFamily: "monospace" }}>{currentPage?.backgroundColor || "#ffffff"}</Box>
												</Box>
											</Box>
											<Box pt={2} style={{ borderTop: "1px solid #e2e8f0" }}>
												<Button variant="contained" fullWidth onClick={applyBackgroundToAllPages}
													style={{ textTransform: "none", padding: "10px 16px", borderRadius: 10, background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)", color: "#fff", boxShadow: "0 2px 8px rgba(124,58,237,0.3)" }}>
													Aplicar a todas las páginas
												</Button>
												<Box fontSize={11} color="#94a3b8" mt={0.75} textAlign="center">Se excluye la página de Registro</Box>
											</Box>
										</Box>
										<Box mb={3} p={2.5} borderRadius={14} style={{ background: "linear-gradient(135deg, #fafbfc 0%, #f1f5f9 100%)", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
											<Box fontSize={13} fontWeight={600} color="#334155" mb={2} display="flex" alignItems="center" style={{ gap: 10 }}>
												<Box p={0.9} borderRadius={10} style={{ background: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
													<SwapHorizIcon style={{ fontSize: 20, color: "#7c3aed" }} />
												</Box>
												Animación (a la página siguiente)
											</Box>
											<Box display="flex" flexDirection="column" style={{ gap: 12 }}>
												<FormControl variant="outlined" size="small" fullWidth>
													<InputLabel>Transición</InputLabel>
													<Select
														value={currentPage?.transitionToNext || "none"}
														onChange={(e) => updatePageDesign({ transitionToNext: e.target.value })}
														label="Transición"
														style={{ fontSize: 14 }}
													>
														{PAGE_TRANSITIONS.map((t) => (
															<MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
														))}
													</Select>
												</FormControl>
												<FormControl variant="outlined" size="small" fullWidth>
													<InputLabel>Velocidad</InputLabel>
													<Select
														value={(() => {
															const d = currentPage?.transitionDuration ?? 350;
															return TRANSITION_SPEEDS.some((s) => s.ms === d) ? d : 350;
														})()}
														onChange={(e) => updatePageDesign({ transitionDuration: Number(e.target.value) })}
														label="Velocidad"
														style={{ fontSize: 14 }}
													>
														{TRANSITION_SPEEDS.map((s) => (
															<MenuItem key={s.ms} value={s.ms}>{s.label}</MenuItem>
														))}
													</Select>
												</FormControl>
												<Button variant="outlined" fullWidth onClick={applyAnimationToAllPages}
													style={{ textTransform: "none", padding: "8px 16px", borderRadius: 10, borderColor: "#7c3aed", color: "#7c3aed" }}>
													Aplicar a todas las páginas
												</Button>
											</Box>
										</Box>
										<Box p={2} textAlign="center" color="#94a3b8" fontSize={12}>
											O selecciona un elemento en el lienzo para editarlo
										</Box>
									</>
								) : addPanelCategory === "configuracion" && isRegistrationPage ? (
									<Box p={2}>
										<Box fontSize={16} fontWeight={700} color="#1e293b" mb={3}>Configuración</Box>
										<Box mb={3} p={2.5} borderRadius={16} style={{ background: "linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
											<Box fontSize={15} fontWeight={600} color="#334155" mb={2} display="flex" alignItems="center" style={{ gap: 10 }}>
												<Box p={1} borderRadius={10} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
													<ImageIcon style={{ fontSize: 22, color: "#64748b" }} />
												</Box>
												Logo de la página de registro
											</Box>
											<Box fontSize={13} color="#64748b" mb={2}>Recomendado: imagen cuadrada 150×150 px</Box>
											<Box onClick={() => setLogoPickerOpen(true)} display="flex" flexDirection="column" alignItems="center" justifyContent="center"
												style={{ minHeight: 160, borderRadius: 16, border: "2px dashed #cbd5e1", background: currentPage?.registrationDesign?.logoUrl ? "#fff" : "#f8fafc", cursor: "pointer", transition: "all 0.2s ease", padding: 20 }}
												onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#7c3aed"; e.currentTarget.style.background = "#f5f3ff"; }}
												onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.background = currentPage?.registrationDesign?.logoUrl ? "#fff" : "#f8fafc"; }}>
												{currentPage?.registrationDesign?.logoUrl ? (
													<>
														<Box style={{ width: 120, height: 120, borderRadius: 16, overflow: "hidden", border: "2px solid #e2e8f0", flexShrink: 0 }}>
															<img src={currentPage.registrationDesign.logoUrl.startsWith("http") || currentPage.registrationDesign.logoUrl.startsWith("/") || currentPage.registrationDesign.logoUrl.startsWith("data:") ? currentPage.registrationDesign.logoUrl : `${SERVER_URL}/${currentPage.registrationDesign.logoUrl}`} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
														</Box>
														<Box fontSize={14} fontWeight={500} color="#64748b" mt={1.5}>Clic para cambiar</Box>
													</>
												) : (
													<>
														<ImageIcon style={{ fontSize: 48, color: "#94a3b8" }} />
														<Box fontSize={15} fontWeight={600} color="#475569" mt={1.5}>Elegir logo</Box>
														<Box fontSize={13} color="#94a3b8" mt={0.5}>De la galería o subir nuevo</Box>
													</>
												)}
											</Box>
											{currentPage?.registrationDesign?.logoUrl && (
												<Button fullWidth size="medium" onClick={(e) => { e.stopPropagation(); updateRegistrationDesign({ logoUrl: "" }); }} style={{ marginTop: 16, textTransform: "none", color: "#64748b" }}>
													Quitar logo
												</Button>
											)}
										</Box>
									</Box>
								) : addPanelCategory === "diseño" && isRegistrationPage ? (
									<Box p={2}>
										<Box fontSize={16} fontWeight={700} color="#1e293b" mb={3}>Diseño de registro</Box>
										<Box mb={3} p={2.5} borderRadius={16} style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
											<Box fontSize={14} fontWeight={600} color="#334155" mb={2}>Fondo</Box>
											<TextField label="URL imagen de fondo" size="small" value={currentPage?.registrationDesign?.backgroundImageUrl || ""} onChange={(e) => updateRegistrationDesign({ backgroundImageUrl: e.target.value })} placeholder="https://..." fullWidth margin="dense" />
											<Box mt={2}>
												<HexColorPickerPopover label="Color de fondo" value={currentPage?.registrationDesign?.backgroundColor || "#ffffff"} onChange={(c) => updateRegistrationDesign({ backgroundColor: c })} />
											</Box>
										</Box>
									</Box>
								) : (
									<>
								{addPanelCategory === "plantillas" && (
									<>
										<Box fontSize={12} fontWeight={600} color="#64748b" mb={1.5} letterSpacing="0.02em">PLANTILLAS</Box>
										{isRegistrationPage ? (
											<Box fontSize={13} color="#94a3b8" p={2}>Las plantillas no se pueden aplicar a la página de registro.</Box>
										) : plantillasLoading ? (
											<Box p={3} textAlign="center" color="#94a3b8" fontSize={13}>Cargando plantillas...</Box>
										) : plantillas.length === 0 ? (
											<Box p={3} textAlign="center" color="#94a3b8" fontSize={13}>No hay plantillas disponibles.</Box>
										) : (
											<Box display="flex" flexDirection="column" style={{ gap: 12 }}>
												{plantillas.map((plt) => (
													<Box key={plt._id || plt.name} onClick={() => applyTemplate(plt)}
														style={{ borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", border: "1px solid #e2e8f0", cursor: "pointer", transition: "all 0.2s ease", background: "#fff" }}
														onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(124,58,237,0.2)"; e.currentTarget.style.borderColor = "#7c3aed"; e.currentTarget.style.transform = "translateY(-2px)"; }}
														onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.transform = "none"; }}>
														<Box style={{ height: 80, background: plt.design?.backgroundColor || "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
															<ViewModuleIcon style={{ fontSize: 36, color: "rgba(0,0,0,0.15)" }} />
														</Box>
														<Box p={1.5}>
															<Box fontSize={14} fontWeight={600} color="#1e293b">{plt.name}</Box>
															{plt.description && <Box fontSize={11} color="#64748b" mt={0.5}>{plt.description}</Box>}
															<Box fontSize={11} color="#7c3aed" fontWeight={500} mt={1}>Aplicar plantilla</Box>
														</Box>
													</Box>
												))}
											</Box>
										)}
									</>
								)}
								{addPanelCategory === "elementos" && (
									<>
										<Box fontSize={12} fontWeight={600} color="#64748b" mb={1.5} letterSpacing="0.02em">FORMAS</Box>
										<Box display="flex" flexWrap="wrap" style={{ gap: 10 }}>
											{[
												{ type: "shape-square", Icon: CropSquareIcon, label: "Cuadrado" },
												{ type: "shape-circle", Icon: PanoramaFishEyeIcon, label: "Círculo" },
												{ type: "shape-triangle", Icon: ChangeHistoryIcon, label: "Triángulo" },
												{ type: "shape-line", Icon: RemoveIcon, label: "Línea" },
											].map(({ type, Icon: ShapeIcon, label }) => (
												<Box key={type} onClick={() => effectiveAddElement(type)} display="flex" flexDirection="column" alignItems="center" justifyContent="center"
													style={{ width: 72, minHeight: 72, padding: "8px 4px", borderRadius: 12, background: "#f8fafc", border: "1px solid #e2e8f0", cursor: "pointer", transition: "all 0.2s ease" }}
													onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.borderColor = "#7c3aed"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(124,58,237,0.15)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
													onMouseLeave={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>
													<ShapeIcon style={{ fontSize: 28, color: "#64748b", marginBottom: 4 }} />
													<Box fontSize={11} color="#64748b" textAlign="center" lineHeight={1.2}>{label}</Box>
												</Box>
											))}
										</Box>
									</>
								)}
								{addPanelCategory === "texto" && (
									<>
										<Box fontSize={12} fontWeight={600} color="#64748b" mb={1.5} letterSpacing="0.02em">TEXTO</Box>
										<Box onClick={() => effectiveAddElement("text")} display="flex" alignItems="center" p={1.5} mb={1.5}
											style={{ borderRadius: 12, background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)", cursor: "pointer", boxShadow: "0 4px 14px rgba(124,58,237,0.35)", transition: "all 0.2s ease" }}
											onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(124,58,237,0.4)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
											onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 4px 14px rgba(124,58,237,0.35)"; e.currentTarget.style.transform = "none"; }}>
											<Box display="flex" alignItems="center" justifyContent="center" style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.2)", marginRight: 12 }}>
												<TitleIcon style={{ fontSize: 22, color: "#fff" }} />
											</Box>
											<Box fontSize={14} fontWeight={600} color="#fff">Añadir caja de texto</Box>
										</Box>
										<Box fontSize={11} fontWeight={600} color="#94a3b8" mb={1} mt={2}>Estilos predeterminados</Box>
										<Box display="flex" flexDirection="column" style={{ gap: 8 }}>
											{[
												{ label: "Título", sub: "Añadir un título", size: 22, bold: true, overrides: { content: { html: "<p>Título</p>" }, style: { fontSize: 28, fontWeight: "bold" }, width: 300, height: 50 } },
												{ label: "Subtítulo", sub: "Añadir un subtítulo", size: 16, bold: false, overrides: { content: { html: "<p>Subtítulo</p>" }, style: { fontSize: 18 }, width: 280, height: 40 } },
												{ label: "Texto", sub: "Añade algo de texto", size: 14, bold: false, overrides: { content: { html: "<p>Texto</p>" }, style: { fontSize: 14 }, width: 250, height: 60 } },
											].map(({ label, sub, size, bold, overrides }) => (
												<Box key={label} onClick={() => effectiveAddElement("text", overrides)} p={1.25}
													style={{ borderRadius: 10, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", transition: "all 0.2s ease" }}
													onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#7c3aed"; e.currentTarget.style.background = "#faf5ff"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(124,58,237,0.1)"; }}
													onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.background = "#fff"; e.currentTarget.style.boxShadow = "none"; }}>
													<Box fontSize={size} fontWeight={bold ? 700 : 400} color="#1e293b" style={{ lineHeight: 1.3 }}>{label}</Box>
													<Box fontSize={11} color="#94a3b8" mt={0.25}>{sub}</Box>
												</Box>
											))}
										</Box>
									</>
								)}
								{addPanelCategory === "quizzes" && (
									<>
										<Box fontSize={12} fontWeight={600} color="#64748b" mb={1.5} letterSpacing="0.02em">PREGUNTAS</Box>
										<Box onClick={async () => {
												try {
													const res = await createQuiz({ fullName: "Pregunta", questions: [{ type: "multipleChoice", text: "Pregunta", options: [{ text: "Opción 1", imageUrl: "", correct: true }], explanation: "" }], pointsCorrect: 10, pointsIncorrect: -5, allowMultipleAttempts: false });
													loadQuizzes();
													setAddPanelCategory("editar");
													effectiveAddElement("quiz", { content: { quizId: res.data._id } });
												} catch (e) { alertError({ error: e, customMessage: "No se pudo crear la pregunta." }); }
											}} display="flex" alignItems="center" p={2} mb={1.5}
											style={{ borderRadius: 12, border: "2px dashed #7c3aed", background: "#faf5ff", cursor: "pointer", transition: "all 0.2s ease" }}
											onMouseEnter={(e) => { e.currentTarget.style.background = "#f5f3ff"; e.currentTarget.style.borderColor = "#5b21b6"; }}
											onMouseLeave={(e) => { e.currentTarget.style.background = "#faf5ff"; e.currentTarget.style.borderColor = "#7c3aed"; }}>
											<Box display="flex" alignItems="center" justifyContent="center" style={{ width: 44, height: 44, borderRadius: 12, background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)", marginRight: 12 }}>
												<AddIcon style={{ fontSize: 24, color: "#fff" }} />
											</Box>
											<Box>
												<Box fontSize={14} fontWeight={600} color="#1e293b">Añadir pregunta</Box>
												<Box fontSize={11} color="#64748b">Nueva pregunta en el canvas</Box>
											</Box>
										</Box>
										<Box fontSize={12} fontWeight={500} color="#64748b" mb={1}>O reutilizar una existente:</Box>
										{quizzesLoading ? (
											<Box p={2} color="#94a3b8" fontSize={13}>Cargando...</Box>
										) : quizzes.length === 0 ? (
											<Box p={2} color="#94a3b8" fontSize={13}>No hay preguntas. Añade una arriba.</Box>
										) : (
											<Box display="flex" flexDirection="column" style={{ gap: 8 }}>
												{quizzes.filter((q) => q && q.fullName).map((q) => (
													<Box key={q._id} onClick={() => effectiveAddElement("quiz", { content: { quizId: q._id } })} display="flex" alignItems="center" p={1.5}
														style={{ borderRadius: 10, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", transition: "all 0.2s ease" }}
														onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#7c3aed"; e.currentTarget.style.background = "#faf5ff"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(124,58,237,0.12)"; }}
														onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.background = "#fff"; e.currentTarget.style.boxShadow = "none"; }}>
														<LiveHelpIcon style={{ fontSize: 22, color: "#7c3aed", marginRight: 10, flexShrink: 0 }} />
														<Box flex={1} minWidth={0}>
															<Box fontSize={13} fontWeight={600} color="#1e293b">{q.fullName}</Box>
															<Box fontSize={11} color="#94a3b8">{((q.questions || [])[0]?.text || "Sin texto").slice(0, 35)}{((q.questions || [])[0]?.text || "").length > 35 ? "…" : ""} · +{q.pointsCorrect ?? 10}/{q.pointsIncorrect ?? -5} pts</Box>
														</Box>
													</Box>
												))}
											</Box>
										)}
									</>
								)}
								{addPanelCategory === "interactivos" && (
									<>
										<Box fontSize={12} fontWeight={600} color="#64748b" mb={1.5} letterSpacing="0.02em">ELEMENTOS INTERACTIVOS</Box>
										<Box display="flex" flexDirection="column" style={{ gap: 10 }}>
											{[
												{ type: "button", Icon: ArrowForwardIcon, label: "Botón", desc: "Botón con acción (p. ej. siguiente página)" },
												{ type: "hint", Icon: LiveHelpIcon, label: "Pista", desc: "Botón que muestra una pista (opcional penalización)" },
												{ type: "image", Icon: ImageIcon, label: "Imagen", desc: "Elegir o subir imagen de multimedia" },
												{ type: "video", Icon: VideoLibraryIcon, label: "Video", desc: "Elegir o subir video de multimedia" },
											].map(({ type, Icon: MediaIcon, label, desc }) => (
												<Box key={type} onClick={() => type === "image" || type === "video" ? (setMediaPickerMode(type), setMediaPickerOpen(true)) : effectiveAddElement(type)} display="flex" alignItems="center" p={1.5}
													style={{ borderRadius: 12, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", transition: "all 0.2s ease" }}
													onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#7c3aed"; e.currentTarget.style.background = "#faf5ff"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(124,58,237,0.12)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
													onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.background = "#fff"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>
													<Box display="flex" alignItems="center" justifyContent="center" style={{ width: 40, height: 40, borderRadius: 10, background: "#f1f5f9", marginRight: 12 }}>
														<MediaIcon style={{ fontSize: 22, color: "#64748b" }} />
													</Box>
													<Box>
														<Box fontSize={14} fontWeight={600} color="#1e293b">{label}</Box>
														<Box fontSize={11} color="#94a3b8">{desc}</Box>
													</Box>
												</Box>
											))}
										</Box>
									</>
								)}
								{addPanelCategory === "multimedia" && (
									<>
										<Box fontSize={12} fontWeight={600} color="#64748b" mb={1.5} letterSpacing="0.02em">MULTIMEDIA</Box>
										<Box fontSize={11} color="#94a3b8" mb={2}>Tus imágenes y videos. Solo tú puedes verlos y usarlos.</Box>
										<input ref={fileInputRef} type="file" accept="image/*,video/*" style={{ display: "none" }} onChange={handleUploadUserMedia} />
										<Box onClick={() => fileInputRef.current?.click()} display="flex" alignItems="center" justifyContent="center" p={2} mb={2}
											style={{ borderRadius: 12, border: "2px dashed #cbd5e1", background: "#f8fafc", cursor: "pointer", transition: "all 0.2s ease" }}
											onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#7c3aed"; e.currentTarget.style.background = "#faf5ff"; }}
											onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.background = "#f8fafc"; }}>
											<CloudUploadIcon style={{ fontSize: 28, color: "#94a3b8", marginRight: 8 }} />
											<Box fontSize={13} fontWeight={600} color="#64748b">Subir imagen o video</Box>
										</Box>
										{userMediaLoading ? (
											<Box p={2} textAlign="center" color="#94a3b8" fontSize={12}>Cargando...</Box>
										) : userMedia.length === 0 ? (
											<Box p={2} textAlign="center" color="#94a3b8" fontSize={12}>No hay archivos. Sube imágenes o videos arriba.</Box>
										) : (
											<Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
												{userMedia.map((item) => (
													<Box
														key={item.path}
														draggable
														onDragStart={(e) => {
															e.dataTransfer.setData("application/ersaas-media", JSON.stringify({ type: item.isVideo ? "video" : "image", path: item.path }));
															e.dataTransfer.effectAllowed = "copy";
														}}
														style={{ width: 72, flexShrink: 0, cursor: "grab" }}
													>
														<Box style={{ width: 72, height: 72, borderRadius: 8, overflow: "hidden", border: "1px solid #e2e8f0", background: "#f1f5f9" }}>
															{item.isVideo ? (
																<video
																	src={item.path.startsWith("http") || item.path.startsWith("/") ? item.path : `${SERVER_URL}/${item.path}`}
																	preload="metadata"
																	muted
																	playsInline
																	style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
																	draggable={false}
																/>
															) : (
																<img src={item.path.startsWith("http") || item.path.startsWith("/") ? item.path : `${SERVER_URL}/${item.path}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }} draggable={false} />
															)}
														</Box>
													</Box>
												))}
											</Box>
										)}
									</>
								)}
							</>
						)}
								</div>
							</Box>
				</>
		</Box>
		</Box>
		</Box>

		<Dialog open={mediaPickerOpen} onClose={() => { setMediaPickerOpen(false); setMediaPickerMode(null); }} maxWidth="sm" fullWidth>
			<DialogTitle>{mediaPickerMode === "image" ? "Elegir imagen" : "Elegir video"}</DialogTitle>
			<DialogContent>
				<Box display="flex" flexWrap="wrap" style={{ gap: 12, marginTop: 8 }}>
					<Box onClick={() => mediaElementFileInputRef.current?.click()} display="flex" flexDirection="column" alignItems="center" justifyContent="center"
						style={{ width: 88, height: 88, borderRadius: 12, border: "2px dashed #cbd5e1", background: "#f8fafc", cursor: "pointer" }}>
						<CloudUploadIcon style={{ fontSize: 32, color: "#94a3b8" }} />
						<Box fontSize={11} color="#64748b" mt={0.5}>Subir</Box>
					</Box>
					<input ref={mediaElementFileInputRef} type="file" accept={mediaPickerMode === "image" ? "image/*" : "video/*"} style={{ display: "none" }} onChange={handleUploadMediaForElement} />
					{userMediaLoading ? (
						<Box p={3} color="#94a3b8" fontSize={13}>Cargando...</Box>
					) : (
						userMedia.filter((m) => mediaPickerMode === "image" ? !m.isVideo : m.isVideo).map((item) => (
							<Box key={item.path} onClick={() => handleSelectMediaForElement(item.path)}
								style={{ width: 88, height: 88, borderRadius: 12, overflow: "hidden", border: "2px solid #e2e8f0", cursor: "pointer" }}>
								{item.isVideo ? (
									<video
										src={item.path.startsWith("http") || item.path.startsWith("/") ? item.path : `${SERVER_URL}/${item.path}`}
										preload="metadata"
										muted
										playsInline
										style={{ width: "100%", height: "100%", objectFit: "cover" }}
									/>
								) : (
									<img src={item.path.startsWith("http") || item.path.startsWith("/") ? item.path : `${SERVER_URL}/${item.path}`} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
								)}
							</Box>
						))
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => { setMediaPickerOpen(false); setMediaPickerMode(null); }}>Cancelar</Button>
			</DialogActions>
		</Dialog>

		<Dialog open={!!quizImagePickerFor} onClose={() => { setQuizImagePickerFor(null); setQuizOptionImageUrl(""); }} maxWidth="sm" fullWidth>
			<DialogTitle>Imagen de la opción {quizImagePickerFor ? quizImagePickerFor.optionIndex + 1 : ""}</DialogTitle>
			<DialogContent>
				{quizImagePickerFor && (() => {
					const pickerEl = (currentPage?.elements || []).find((e) => e.elementId === quizImagePickerFor.elementId);
					const quizId = pickerEl?.content?.quizId;
					const quiz = quizzes.find((q) => q._id === quizId);
					const firstQ = quiz?.questions?.[0];
					const applyImage = (url) => {
						if (!quizId || !handleUpdateQuiz || !firstQ) return;
						const newOpts = (firstQ.options || []).map((o, j) => j === quizImagePickerFor.optionIndex ? { ...o, imageUrl: url || "" } : o);
						handleUpdateQuiz(quizId, { fullName: quiz.fullName, questions: [{ ...firstQ, options: newOpts }], pointsCorrect: quiz.pointsCorrect, pointsIncorrect: quiz.pointsIncorrect, allowMultipleAttempts: quiz.allowMultipleAttempts });
						setQuizImagePickerFor(null);
						setQuizOptionImageUrl("");
					};
					return (
						<Box display="flex" flexDirection="column" style={{ gap: 12, marginTop: 8 }}>
							<Box display="flex" style={{ gap: 8 }}>
								<TextField fullWidth size="small" placeholder="Pega la URL de la imagen" value={quizOptionImageUrl} onChange={(e) => setQuizOptionImageUrl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") applyImage(quizOptionImageUrl); }} />
								<Button variant="contained" size="small" onClick={() => applyImage(quizOptionImageUrl)}>Aplicar</Button>
							</Box>
							<Box fontSize={12} color="#64748b">Galería:</Box>
							<Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
								{userMediaLoading ? (
									<Box p={2} color="#94a3b8" fontSize={13}>Cargando...</Box>
								) : (
									userMedia.filter((m) => !m.isVideo).slice(0, 12).map((item) => (
										<Box key={item.path} onClick={() => applyImage(item.path.startsWith("http") || item.path.startsWith("/") ? item.path : `${SERVER_URL}/${item.path}`)}
											style={{ width: 64, height: 64, borderRadius: 8, overflow: "hidden", border: "2px solid #e2e8f0", cursor: "pointer" }}>
											<img src={item.path.startsWith("http") || item.path.startsWith("/") ? item.path : `${SERVER_URL}/${item.path}`} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
										</Box>
									))
								)}
							</Box>
						</Box>
					);
				})()}
			</DialogContent>
			<DialogActions>
				<Button onClick={() => { setQuizImagePickerFor(null); setQuizOptionImageUrl(""); }}>Cerrar</Button>
			</DialogActions>
		</Dialog>

		<Dialog open={backgroundPickerOpen} onClose={() => setBackgroundPickerOpen(false)} maxWidth="sm" fullWidth>
			<DialogTitle>Elegir imagen de fondo</DialogTitle>
			<DialogContent>
				<Box display="flex" flexWrap="wrap" style={{ gap: 12, marginTop: 8 }}>
					<Box onClick={() => backgroundFileInputRef.current?.click()} display="flex" flexDirection="column" alignItems="center" justifyContent="center"
						style={{ width: 88, height: 88, borderRadius: 12, border: "2px dashed #cbd5e1", background: "#f8fafc", cursor: "pointer" }}>
						<CloudUploadIcon style={{ fontSize: 32, color: "#94a3b8" }} />
						<Box fontSize={11} color="#64748b" mt={0.5}>Subir</Box>
					</Box>
					{userMediaLoading ? (
						<Box p={3} color="#94a3b8" fontSize={13}>Cargando...</Box>
					) : (
						userMedia.filter((m) => !m.isVideo).map((item) => (
							<Box key={item.path} onClick={() => handleSelectBackgroundImage(item.path)}
								style={{ width: 88, height: 88, borderRadius: 12, overflow: "hidden", border: "2px solid #e2e8f0", cursor: "pointer", background: currentPage?.backgroundImageUrl === item.path ? "#7c3aed" : "#f1f5f9" }}>
								<img src={item.path.startsWith("http") || item.path.startsWith("/") ? item.path : `${SERVER_URL}/${item.path}`} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
							</Box>
						))
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setBackgroundPickerOpen(false)}>Cancelar</Button>
			</DialogActions>
		</Dialog>

		<Dialog open={logoPickerOpen} onClose={() => setLogoPickerOpen(false)} maxWidth="sm" fullWidth>
			<DialogTitle>Elegir logo</DialogTitle>
			<DialogContent>
				<Box fontSize={12} color="#64748b" mb={2}>Recomendado: imagen cuadrado 150×150 px</Box>
				<input ref={logoFileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleUploadAndSelectLogo} />
				<Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
					<Box onClick={() => logoFileInputRef.current?.click()} display="flex" flexDirection="column" alignItems="center" justifyContent="center"
						style={{ width: 88, height: 88, borderRadius: 12, border: "2px dashed #cbd5e1", background: "#f8fafc", cursor: "pointer" }}>
						<CloudUploadIcon style={{ fontSize: 32, color: "#94a3b8" }} />
						<Box fontSize={11} color="#64748b" mt={0.5}>Subir</Box>
					</Box>
					{userMediaLoading ? (
						<Box p={3} color="#94a3b8" fontSize={13}>Cargando...</Box>
					) : (
						userMedia.filter((m) => !m.isVideo).map((item) => (
							<Box key={item.path} onClick={() => handleSelectLogo(item.path)}
								style={{ width: 88, height: 88, borderRadius: 12, overflow: "hidden", border: "2px solid #e2e8f0", cursor: "pointer", background: currentPage?.registrationDesign?.logoUrl === item.path ? "#7c3aed" : "#f1f5f9" }}>
								<img src={item.path.startsWith("http") || item.path.startsWith("/") ? item.path : `${SERVER_URL}/${item.path}`} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
							</Box>
						))
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setLogoPickerOpen(false)}>Cancelar</Button>
			</DialogActions>
		</Dialog>

		</>
	);
}